(() => {
// CF Redeploy 02/01/2026 - Secrets Hidden
/**
 * DataManager - Handles all CMS data fetching, caching, and lookups
 * Single source of truth for all project data from Webflow API
 */ class $48378b2ee7bf424c$var$DataManager {
    static CONFIG = {
        API_ENDPOINT: 'https://faasgis-eky.pages.dev/api/tokens',
        CACHE_DURATION: 600000,
        RETRY_ATTEMPTS: 3,
        RETRY_DELAY: 1000
    };
    constructor(){
        // ! Raw data storage - Read "Array Object" from API_ENDPOINT
        this.cmsData = null;
        this.typologies = null;
        this.statuses = null;
        this.projectTypologyCode = null;
        this.projectStatusCode = null;
        this.mapboxToken = null;
        // ! Lookup maps (for fast access)
        this.typologyColorMap = new Map(); // typologyId -> color
        this.typologyNameMap = new Map(); // typologyId -> name
        this.typologySlugToIdMap = new Map(); // typologySlug -> typologyId
        this.statusNameMap = new Map(); // statusId -> name
        this.statusSlugToIdMap = new Map(); // statusSlug -> statusId
        // ! GeoJSON storage
        this.allLocations = [];
        this.visibleLocations = {
            type: "FeatureCollection",
            features: []
        };
        // ! Cache management
        this.lastFetchTime = null;
        this.isFetching = false;
    }
    /**
     * ! Fetch data from API with caching and retry logic
     * @returns {Promise<Object>} Raw API response data
     */ async fetchData() {
        if (this._isCacheValid()) return this._getCachedResponse();
        if (this.isFetching) return this._waitForFetch();
        this.isFetching = true;
        let lastError = null;
        // ! Retry logic
        for(let attempt = 1; attempt <= $48378b2ee7bf424c$var$DataManager.CONFIG.RETRY_ATTEMPTS; attempt++)try {
            const response = await fetch($48378b2ee7bf424c$var$DataManager.CONFIG.API_ENDPOINT);
            if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            const data = await response.json();
            this._validateResponse(data); // ! Validate response structure
            this._storeData(data); // ! Store raw data
            this._buildLookupMaps(); // ! Build lookup maps
            this.lastFetchTime = Date.now(); // ! Mark as successfully fetched
            this.isFetching = false; // ! Mark fetch as complete
            return data;
        } catch (error) {
            lastError = error;
            // Wait before retrying (except on last attempt)
            if (attempt < $48378b2ee7bf424c$var$DataManager.CONFIG.RETRY_ATTEMPTS) await this._sleep($48378b2ee7bf424c$var$DataManager.CONFIG.RETRY_DELAY * attempt);
        }
        this.isFetching = false; // ! All retries failed
        throw new Error(`Failed to fetch data after ${$48378b2ee7bf424c$var$DataManager.CONFIG.RETRY_ATTEMPTS} attempts: ${lastError.message}`);
    }
    /**
     * ! Process CMS data into GeoJSON format
     * Filters out invalid items and builds feature collection
     */ processGeoData() {
        this.allLocations = [];
        let skippedCount = 0;
        this.cmsData.forEach((item, index)=>{
            const fields = item.fieldData;
            const projectName = fields.name; // ! Extract core fields
            const locationID = fields.slug; //! use slug as unique ID
            const locationLat = fields.latitude;
            const locationLong = fields.longitude;
            const typologyCodeId = fields['typology-code'];
            const statusCodeId = fields['status-code'];
            const showProject = fields['show-project'];
            const photoThumbnail = fields['photo-thumbnail'];
            const isFeatured = fields['featured'] || false;
            // ! Validation: SKIP items without coordinates
            if (!locationLat || !locationLong) {
                skippedCount++;
                return;
            }
            // ! Validation: SKIP items marked as hidden
            if (showProject === false) {
                skippedCount++;
                return;
            }
            const geoFeature = {
                type: "Feature",
                geometry: {
                    type: "Point",
                    coordinates: [
                        parseFloat(locationLong),
                        parseFloat(locationLat)
                    ]
                },
                properties: {
                    id: locationID,
                    projectName: projectName,
                    arrayID: index,
                    typologyId: typologyCodeId,
                    typologyName: this.getTypologyName(typologyCodeId),
                    statusId: statusCodeId,
                    statusName: this.getStatusName(statusCodeId),
                    photoThumbnailUrl: photoThumbnail?.url || null,
                    isFeatured: isFeatured
                }
            };
            this.allLocations.push(geoFeature);
        });
        this.visibleLocations.features = [
            ...this.allLocations
        ]; // Update visible locations (initially same as all)
        console.log(`data-m: \u{2705} Processed ${this.allLocations.length} valid locations (${skippedCount} skipped)`);
    }
    // ! TYPOLOGY LOOKUPS
    /**
     * ! GET TYPOLOGY [COLOR] BY [ID]
     * @param {string} typologyId - Webflow typology item ID
     * @returns {string} Hex color code
     */ getTypologyColor(typologyId) {
        return this.typologyColorMap.get(typologyId) || '#000000'; // Default: black
    }
    /**
     * ! GET TYPOLOGY [NAME] BY [ID]
     * @param {string} typologyId - Webflow typology item ID
     * @returns {string} Typology name
     */ getTypologyName(typologyId) {
        return this.typologyNameMap.get(typologyId) || 'Unknown';
    }
    /**
     * Get typology ID from slug
     * @param {string} slug - Typology slug from Webflow
     * @returns {string|null} Typology ID or null if not found
     */ getTypologyIdFromSlug(slug) {
        return this.typologySlugToIdMap.get(slug) || null;
    }
    // ! STATUS LOOKUPS
    /**
     * ! GET STATUS [NAME] BY [ID]
     * @param {string} statusId - Webflow status item ID
     * @returns {string} Status name
     */ getStatusName(statusId) {
        return this.statusNameMap.get(statusId) || 'Unknown';
    }
    /**
     * Get status ID from slug
     * @param {string} slug - Status slug from Webflow
     * @returns {string|null} Status ID or null if not found
     */ getStatusIdFromSlug(slug) {
        return this.statusSlugToIdMap.get(slug) || null;
    }
    // ! FILTERING
    /**
     * Filter locations by criteria
     * @param {Object} filters - Filter criteria
     * @returns {Array} Filtered GeoJSON features
     */ filterLocations(filters = {}) {
        const { typologyIds: typologyIds = [], statusIds: statusIds = [] } = filters;
        let filtered = [
            ...this.allLocations
        ];
        // ! Filter by typology
        if (typologyIds.length > 0) filtered = filtered.filter((feature)=>typologyIds.includes(feature.properties.typologyId));
        // ! Filter by status
        if (statusIds.length > 0) filtered = filtered.filter((feature)=>statusIds.includes(feature.properties.statusId));
        this.visibleLocations.features = filtered;
        return filtered;
    }
    // ==================== PRIVATE METHODS ====================
    /**
     * Validate API response structure
     * @private
     */ _validateResponse(data) {
        if (!data.success) throw new Error('API response indicates failure');
        if (!data.mapboxToken) throw new Error('Missing Mapbox token in response');
        if (!Array.isArray(data.items)) throw new Error('Missing or invalid items array in response');
        if (!Array.isArray(data.typologies)) throw new Error('Missing or invalid typologies array in response');
        if (!Array.isArray(data.statuses)) throw new Error('Missing or invalid statuses array in response');
    }
    /**
     * ! Store raw API data
     * @private
     */ _storeData(data) {
        this.cmsData = data.items;
        this.typologies = data.typologies;
        this.statuses = data.statuses;
        this.mapboxToken = data.mapboxToken;
        this.projectTypologyCode = data.projectTypologyCode;
        this.projectStatusCode = data.projectStatusCode;
    }
    /**
     * ! Build fast lookup maps for typologies and statuses
     * @private
     */ _buildLookupMaps() {
        // ! Clear existing maps
        this.typologyColorMap.clear();
        this.typologyNameMap.clear();
        this.typologySlugToIdMap.clear();
        this.statusNameMap.clear();
        this.statusSlugToIdMap.clear();
        // ! Build typology maps
        this.typologies.forEach((typology)=>{
            const id = typology.id;
            const name = typology.fieldData.name;
            const color = typology.fieldData.color;
            const slug = typology.fieldData.slug;
            this.typologyColorMap.set(id, color);
            this.typologyNameMap.set(id, name);
            // ! Map slug to ID for DOM interaction
            if (slug) this.typologySlugToIdMap.set(slug, id);
        });
        // ! Build status maps
        this.statuses.forEach((status)=>{
            const id = status.id;
            const name = status.fieldData.name;
            const slug = status.fieldData.slug;
            this.statusNameMap.set(id, name);
            // ! Map slug to ID for DOM interaction
            if (slug) this.statusSlugToIdMap.set(slug, id);
        });
    }
    /**
     * Check if cached data is still valid
     * @private
     */ _isCacheValid() {
        if (!this.lastFetchTime || !this.cmsData) return false;
        const cacheAge = Date.now() - this.lastFetchTime;
        return cacheAge < $48378b2ee7bf424c$var$DataManager.CONFIG.CACHE_DURATION;
    }
    /**
     * Get cached response object
     * @private
     */ _getCachedResponse() {
        return {
            success: true,
            mapboxToken: this.mapboxToken,
            items: this.cmsData,
            typologies: this.typologies,
            count: this.cmsData.length,
            cached: true
        };
    }
    /**
     * Wait for ongoing fetch to complete
     * @private
     */ async _waitForFetch() {
        while(this.isFetching)await this._sleep(100);
        return this._getCachedResponse();
    }
    /**
     * Sleep utility for retry delays
     * @private
     */ _sleep(ms) {
        return new Promise((resolve)=>setTimeout(resolve, ms));
    }
}
var //! EXPORT
$48378b2ee7bf424c$export$2e2bcd8739ae039 = $48378b2ee7bf424c$var$DataManager;
// Legacy support for non-module environments (Webflow, older browsers)
if (typeof window !== 'undefined') window.DataManager = $48378b2ee7bf424c$var$DataManager;


/**
 * MapManager - Handles map initialization, styles, camera, and controls
 * Manages Mapbox GL JS instance and configuration
 */ class $68b7d625d290d5d0$var$MapManager {
    static CONFIG = {
        CONTAINER_ID: 'map',
        STYLE_URL: 'mapbox://styles/faasintra/cmk4jc0ct005u01ss0t15d6fy?optimize=true',
        SATELLITE_STYLE_URL: 'mapbox://styles/faasintra/cmk4jeu9u008n01svbj603g97',
        ATTRIBUTION: "\xa9 FAAS Architecture",
        // ! Desktop defaults
        DEFAULT_CENTER: {
            lng: -97.18372,
            lat: 58.80331
        },
        DEFAULT_ZOOM: 3.5,
        DEFAULT_BEARING: 0,
        DEFAULT_PITCH: 30.50,
        // ! Mobile defaults
        MOBILE_ZOOM: 1.5,
        MOBILE_PITCH: 40,
        // ! Intro animation
        INTRO_ZOOM_DESKTOP: 3.8,
        INTRO_ZOOM_MOBILE: 2,
        INTRO_DURATION: 7500,
        INTRO_SPEED: 0.25,
        INTRO_CURVE: 1,
        ANTIALIAS: true,
        OPTIMIZE: true
    };
    /**
     * ! Create MapManager instance
     * @param {Object} options - Optional configuration overrides
     */ constructor(options = {}){
        // ! Map instance
        this.map = null;
        // ! Reactive mobile detection
        this.isMobile = false;
        this._setupMobileMediaQuery();
        // ! Configuration (merge with defaults, adjust for mobile)
        const baseConfig = {
            ...$68b7d625d290d5d0$var$MapManager.CONFIG
        };
        if (this.isMobile) {
            baseConfig.DEFAULT_ZOOM = $68b7d625d290d5d0$var$MapManager.CONFIG.MOBILE_ZOOM;
            baseConfig.DEFAULT_PITCH = $68b7d625d290d5d0$var$MapManager.CONFIG.MOBILE_PITCH;
        }
        this.config = {
            ...baseConfig,
            ...options
        };
        // ! State
        this.isInitialized = false;
        this.isStyleLoaded = false;
        this.isSatelliteView = false;
        this.dataLayers = []; // * Track data layers to preserve during style switch
        // Event callbacks
        this.onLoadCallbacks = [];
        this.onErrorCallbacks = [];
    }
    /**
     * Setup reactive mobile detection with MediaQuery listener
     * Uses 767px breakpoint for UI responsiveness
     * @private
     */ _setupMobileMediaQuery() {
        const mediaQuery = window.matchMedia('(max-width: 767px)');
        this.isMobile = mediaQuery.matches;
        // Listen for breakpoint changes
        mediaQuery.addEventListener('change', (e)=>{
            this.isMobile = e.matches;
        });
    }
    /**
     * ! Initialize Mapbox map with token
     * @param {string} accessToken - Mapbox GL access token -- from tokens.js > DataManager
     * @returns {Promise<mapboxgl.Map>} Initialized map instance
     */ async initialize(accessToken) {
        if (this.isInitialized) return this.map;
        try {
            // ! Set access token
            mapboxgl.accessToken = accessToken;
            // ! Create map instance
            this.map = new mapboxgl.Map({
                style: this.config.STYLE_URL,
                container: this.config.CONTAINER_ID,
                center: this.config.DEFAULT_CENTER,
                zoom: this.config.DEFAULT_ZOOM,
                bearing: this.config.DEFAULT_BEARING,
                pitch: this.config.DEFAULT_PITCH,
                antialias: this.config.ANTIALIAS,
                customAttribution: this.config.ATTRIBUTION
            });
            this._setupEventListeners(); // ! Set up event listeners
            this._addMapControls(); // ! Add map controls
            await this._waitForStyleLoad(); // ! Wait for style to load
            this._playIntroAnimation(); // ! Start intro animation
            this.isInitialized = true;
            return this.map;
        } catch (error) {
            this._triggerErrorCallbacks(error);
            throw error;
        }
    }
    /**
     * Wait for map style to fully load
     * @returns {Promise<void>}
     * @private
     */ _waitForStyleLoad() {
        return new Promise((resolve, reject)=>{
            if (this.map.isStyleLoaded()) {
                this.isStyleLoaded = true;
                resolve();
                return;
            }
            this.map.on('load', ()=>{
                this.isStyleLoaded = true;
                this._triggerLoadCallbacks();
                resolve();
            });
            this.map.on('error', (error)=>{
                this._triggerErrorCallbacks(error);
                reject(error);
            });
            // * Timeout after 30 seconds
            setTimeout(()=>{
                if (!this.isStyleLoaded) {
                    const error = new Error('Map load timeout after 30 seconds');
                    reject(error);
                }
            }, 30000);
        });
    }
    /**
     * Set up map event listeners
     * @private
     */ _setupEventListeners() {
        this.map.on('movestart', ()=>{});
        this.map.on('moveend', ()=>{});
    }
    /**
     * Add map controls (geolocation with custom button)
     * @private
     */ _addMapControls() {
        // Create geolocate control but don't add it to the map (we'll use custom button)
        this.geolocateControl = new mapboxgl.GeolocateControl({
            positionOptions: {
                enableHighAccuracy: true // Use GPS if available
            },
            trackUserLocation: true,
            showUserHeading: true,
            showUserLocation: true // Show blue dot for user location
        });
        this.map.addControl(this.geolocateControl, 'top-left'); // Add Northing control, REQUIRED for functionality - ".is--hidden" is applied via CSS)
        this._setupCustomGeolocateButton(); // Connect custom button to geolocate control
        this._setupSatelliteToggle(); // Setup satellite toggle button
    }
    /**
     * Setup custom geolocate button to trigger native Mapbox geolocation
     * @private
     */ _setupCustomGeolocateButton() {
        const customButton = document.getElementById('geolocate');
        if (!customButton) return;
        customButton.addEventListener('click', ()=>{
            if (this.geolocateControl) this.geolocateControl.trigger();
        });
        // ! Listen to geolocate events to update button state
        this.geolocateControl.on('geolocate', ()=>{
            customButton.classList.add('is--active');
        });
        this.geolocateControl.on('trackuserlocationstart', ()=>{
            customButton.classList.add('is--active');
        });
        this.geolocateControl.on('trackuserlocationend', ()=>{
            customButton.classList.remove('is--active');
        });
        this.geolocateControl.on('error', ()=>{
            customButton.classList.remove('is--active');
        });
    }
    /**
     * Register callback for map load event
     * @param {Function} callback - Function to call when map loads
     */ onLoad(callback) {
        if (typeof callback !== 'function') throw new Error('Callback must be a function');
        if (this.isStyleLoaded) callback(this.map); // If already loaded, call immediately
        else this.onLoadCallbacks.push(callback); // Otherwise, queue it
    }
    /**
     * Register callback for map error event
     * @param {Function} callback - Function to call on error
     */ onError(callback) {
        if (typeof callback !== 'function') throw new Error('Callback must be a function');
        this.onErrorCallbacks.push(callback);
    }
    /**
     * Trigger all registered load callbacks
     * @private
     */ _triggerLoadCallbacks() {
        this.onLoadCallbacks.forEach((callback)=>{
            try {
                callback(this.map);
            } catch (error) {}
        });
    }
    /**
     * Play intro fly-to animation
     * Smoothly zooms in from initial view to final view
     * @private
     */ _playIntroAnimation() {
        const targetZoom = this.isMobile ? $68b7d625d290d5d0$var$MapManager.CONFIG.INTRO_ZOOM_MOBILE : $68b7d625d290d5d0$var$MapManager.CONFIG.INTRO_ZOOM_DESKTOP;
        const targetPitch = this.isMobile ? $68b7d625d290d5d0$var$MapManager.CONFIG.MOBILE_PITCH : $68b7d625d290d5d0$var$MapManager.CONFIG.DEFAULT_PITCH;
        this.map.flyTo({
            center: $68b7d625d290d5d0$var$MapManager.CONFIG.DEFAULT_CENTER,
            zoom: targetZoom,
            pitch: targetPitch,
            bearing: $68b7d625d290d5d0$var$MapManager.CONFIG.DEFAULT_BEARING,
            duration: $68b7d625d290d5d0$var$MapManager.CONFIG.INTRO_DURATION,
            speed: $68b7d625d290d5d0$var$MapManager.CONFIG.INTRO_SPEED,
            curve: $68b7d625d290d5d0$var$MapManager.CONFIG.INTRO_CURVE,
            essential: true,
            easing: (t)=>1 - Math.pow(1 - t, 6) // easeOutSextic
        });
    }
    /**
     * Trigger all registered error callbacks
     * @private
     */ _triggerErrorCallbacks(error) {
        this.onErrorCallbacks.forEach((callback)=>{
            try {
                callback(error);
            } catch (err) {}
        });
    }
    /**
     * Setup satellite toggle button
     * @private
     */ _setupSatelliteToggle() {
        const toggleBtn = document.getElementById('toggle-satellite');
        if (!toggleBtn) return;
        toggleBtn.addEventListener('click', ()=>{
            this.toggleSatelliteView();
        });
    }
    /**
     * Toggle between standard and satellite view
     * Preserves all data layers (clusters, heatmap, markers)
     * @public
     */ toggleSatelliteView() {
        if (!this.map) return;
        const toggleBtn = document.getElementById('toggle-satellite');
        this._saveDataLayers(); // ! Save current data layers before style switch
        this.isSatelliteView = !this.isSatelliteView; // Toggle state
        const newStyle = this.isSatelliteView // Switch style
         ? $68b7d625d290d5d0$var$MapManager.CONFIG.SATELLITE_STYLE_URL : this.config.STYLE_URL;
        this.map.setStyle(newStyle);
        if (toggleBtn) {
            if (this.isSatelliteView) toggleBtn.classList.add('is--active');
            else toggleBtn.classList.remove('is--active');
        }
        this._toggleNavigationBlendMode(); // Toggle navigation blend mode
        this.map.once('style.load', ()=>{
            this._restoreDataLayers();
        });
    }
    /**
     * ! Save references to data layers before style switch (For Satellite toggle)
     * @private
     */ _saveDataLayers() {
        if (!this.map) return;
        this.dataLayers = [];
        const style = this.map.getStyle(); // Get all sources and layers that contain our data
        if (style && style.sources) Object.keys(style.sources).forEach((sourceId)=>{
            if (sourceId.includes('locations-')) {
                const source = this.map.getSource(sourceId);
                if (source && source._data) this.dataLayers.push({
                    sourceId: sourceId,
                    type: 'geojson',
                    data: source._data,
                    options: style.sources[sourceId]
                });
            }
        });
        if (style && style.layers) style.layers.forEach((layer)=>{
            if (layer.source && layer.source.includes('locations-')) {
                const existingLayer = this.dataLayers.find((d)=>d.sourceId === layer.source);
                if (existingLayer) {
                    if (!existingLayer.layers) existingLayer.layers = [];
                    existingLayer.layers.push(layer); // Add layer to existing source entry
                }
            }
        });
    }
    /**
     * ! Restore data layers after style switch (For Satellite toggle)
     * @private
     */ _restoreDataLayers() {
        if (!this.map || this.dataLayers.length === 0) return;
        this.dataLayers.forEach((layerData)=>{
            if (!this.map.getSource(layerData.sourceId)) {
                const sourceConfig = {
                    type: layerData.type,
                    data: layerData.data
                };
                if (layerData.options.cluster !== undefined) {
                    sourceConfig.cluster = layerData.options.cluster;
                    sourceConfig.clusterMaxZoom = layerData.options.clusterMaxZoom;
                    sourceConfig.clusterRadius = layerData.options.clusterRadius;
                    if (layerData.options.clusterMinPoints) sourceConfig.clusterMinPoints = layerData.options.clusterMinPoints;
                }
                this.map.addSource(layerData.sourceId, sourceConfig);
            }
            if (layerData.layers) layerData.layers.forEach((layer)=>{
                if (!this.map.getLayer(layer.id)) this.map.addLayer(layer);
            });
        });
        // Notify that layers are restored (markers need to be re-rendered)
        if (window.markerManager && window.markerManager.markers.length > 0) {
            const currentZoom = this.map.getZoom(); // Ensure marker visibility is correct when restoring data on map style change
            if (window.markerManager.toggleMarkersVisibility) window.markerManager.toggleMarkersVisibility(currentZoom);
        }
    }
    /**
     * ! Toggle navigation blend mode based on satellite view state
     * @private
     */ _toggleNavigationBlendMode() {
        const navigationEl = document.querySelector('.navigation');
        if (!navigationEl) return;
        if (this.isSatelliteView) navigationEl.style.mixBlendMode = 'normal'; // Satellite mode ON - turn off blend mode difference
        else navigationEl.style.mixBlendMode = 'difference'; // Standard mode - restore blend mode difference
    }
    /**
     * Fly to specific coordinates
     * @param {Object} options - Fly-to options
     * @param {Array|Object} options.center - [lng, lat] or {lng, lat}
     * @param {number} options.zoom - Target zoom level
     * @param {number} options.bearing - Target bearing (rotation)
     * @param {number} options.pitch - Target pitch (tilt)
     * @param {number} options.duration - Animation duration in ms
     */ flyTo(options = {}) {
        if (!this.map) return;
        const flyOptions = {
            center: options.center,
            zoom: options.zoom !== undefined ? options.zoom : this.map.getZoom(),
            bearing: options.bearing !== undefined ? options.bearing : this.map.getBearing(),
            pitch: options.pitch !== undefined ? options.pitch : this.map.getPitch(),
            duration: options.duration !== undefined ? options.duration : 2000,
            essential: true
        };
        this.map.flyTo(flyOptions);
    }
    /**
     * Jump to specific coordinates (no animation)
     * @param {Object} options - Jump-to options
     */ jumpTo(options = {}) {
        if (!this.map) return;
        this.map.jumpTo({
            center: options.center,
            zoom: options.zoom,
            bearing: options.bearing,
            pitch: options.pitch
        });
    }
    /**
     * Zoom in by one level
     * @param {number} duration - Animation duration in ms (default: 300)
     */ zoomIn(duration = 300) {
        if (!this.map) return;
        const currentZoom = this.map.getZoom();
        this.map.easeTo({
            zoom: currentZoom + 1,
            duration: duration
        });
    }
    /**
     * Zoom out by one level
     * @param {number} duration - Animation duration in ms (default: 300)
     */ zoomOut(duration = 300) {
        if (!this.map) return;
        const currentZoom = this.map.getZoom();
        this.map.easeTo({
            zoom: currentZoom - 1,
            duration: duration
        });
    }
    /**
     * Reset map orientation (bearing and pitch) to default
     * @param {number} duration - Animation duration in ms (default: 500)
     */ resetOrientation(duration = 500) {
        if (!this.map) return;
        this.map.easeTo({
            bearing: this.config.DEFAULT_BEARING,
            pitch: this.config.DEFAULT_PITCH,
            duration: duration
        });
    }
    /**
     * Smart fly out - zooms out intelligently or returns to default view
     * If zoom > 8: zoom out to 65% of current zoom
     * If zoom ≤ 8: fly back to initial camera position
     */ flyOut() {
        if (!this.map) return;
        const currentZoom = this.map.getZoom();
        if (currentZoom > 8) this.map.flyTo({
            zoom: currentZoom * 0.65,
            speed: 0.125,
            curve: 2,
            duration: 2500,
            essential: true,
            pitch: 0,
            bearing: 0,
            padding: {
                top: 0,
                bottom: 0,
                left: 0,
                right: 0
            },
            easing: (t)=>1 - Math.pow(1 - t, 6)
        });
        else this.map.flyTo({
            center: this.config.DEFAULT_CENTER,
            zoom: this.config.DEFAULT_ZOOM,
            curve: 2,
            speed: 0.125,
            duration: 3000,
            essential: true,
            pitch: this.config.DEFAULT_PITCH,
            bearing: this.config.DEFAULT_BEARING,
            padding: {
                top: 0,
                bottom: 0,
                left: 0,
                right: 0
            },
            easing: (t)=>1 - Math.pow(1 - t, 6)
        });
    }
    /**
     * Reset camera to default position
     */ resetCamera() {
        this.flyTo({
            center: this.config.DEFAULT_CENTER,
            zoom: this.config.DEFAULT_ZOOM,
            bearing: this.config.DEFAULT_BEARING,
            pitch: this.config.DEFAULT_PITCH
        });
    }
    /**
     * Get current camera position
     * @returns {Object} Current camera state
     */ getCameraState() {
        if (!this.map) return null;
        return {
            center: this.map.getCenter(),
            zoom: this.map.getZoom(),
            bearing: this.map.getBearing(),
            pitch: this.map.getPitch()
        };
    }
    /**
     * Set map style
     * @param {string} styleUrl - Mapbox style URL
     */ setStyle(styleUrl) {
        if (!this.map) return;
        this.map.setStyle(styleUrl);
    }
    /**
     * Resize map (useful after container size change)
     */ resize() {
        if (!this.map) return;
        this.map.resize();
    }
    /**
     * Get the underlying Mapbox GL map instance
     * @returns {mapboxgl.Map|null} Map instance
     */ getMap() {
        return this.map;
    }
    /**
     * Check if map is ready for use
     * @returns {boolean} True if map is initialized and style is loaded
     */ isReady() {
        return this.isInitialized && this.isStyleLoaded;
    }
    /**
     * Destroy map instance and clean up
     */ destroy() {
        if (this.map) {
            this.map.remove();
            this.map = null;
            this.isInitialized = false;
            this.isStyleLoaded = false;
        }
    }
}
var // ! EXPORT
$68b7d625d290d5d0$export$2e2bcd8739ae039 = $68b7d625d290d5d0$var$MapManager;
// Legacy support for non-module environments
if (typeof window !== 'undefined') window.MapManager = $68b7d625d290d5d0$var$MapManager;


/**
 * MarkerManager - Handles DOM marker rendering, styling, popups, and interactions
 * Manages Mapbox GL JS DOM markers lifecycle
 */ class $ef2c6018bdbcf2f4$var$MarkerManager {
    static CONFIG = {
        // Zoom threshold for showing markers
        MIN_ZOOM: 11,
        // Marker types
        MARKER_TYPES: {
            FEATURED: 'featured',
            NON_FEATURED_WITH_PHOTO: 'with-photo',
            NON_FEATURED_NO_PHOTO: 'no-photo' // Type C: Small (12px)
        },
        // Marker sizes
        SIZES: {
            FEATURED_BASE: '50px',
            FEATURED_HOVER: '75px',
            WITH_PHOTO_BASE: '40px',
            WITH_PHOTO_HOVER: '75px',
            NO_PHOTO_BASE: '16px',
            NO_PHOTO_HOVER: '24px' // 1.5x of 16px
        },
        // Z-index management
        Z_INDEX: {
            BASE: 1,
            ACTIVE: 2,
            HOVER: 3
        },
        // Marker styling
        MARKER: {
            BORDER_RADIUS: '50%',
            BORDER_WIDTH: '2px',
            BORDER_COLOR_DEFAULT: 'white',
            CURSOR: 'pointer',
            CIRCLE_SIZE: '8px',
            CIRCLE_OFFSET: '4px'
        },
        // Zoom levels for progressive zooming (matching legacy app)
        ZOOM: {
            FIRST_CLICK: 13,
            SECOND_CLICK: 15,
            THIRD_CLICK: 18.5,
            THRESHOLD_LOW: 12.5,
            THRESHOLD_MID: 14.5,
            THRESHOLD_HIGH: 18 // Below this, zoom to THIRD_CLICK
        },
        // Fly animation settings
        FLY: {
            SPEED: 0.1,
            CURVE: 2,
            DURATION_FAST: 7500,
            DURATION_SLOW: 10000,
            PITCH_3D: 20,
            PITCH_FLAT: 0,
            BEARING: 0
        }
    };
    /**
     * Create MarkerManager instance
     * @param {Object} mapManager - MapManager instance
     * @param {Object} dataManager - DataManager instance
     */ constructor(mapManager, dataManager){
        if (!mapManager || !dataManager) throw new Error('MarkerManager requires both mapManager and dataManager');
        this.mapManager = mapManager;
        this.dataManager = dataManager;
        // Storage for all marker instances
        this.markers = [];
        // State
        this.markersRendered = false;
        this.zoomHandler = null;
        this.activeMarker = null;
        this.activeMarkerSlug = null;
        // Set up zoom-based visibility
        this._setupZoomHandler();
        // Set up map click handler for deactivation
        this._setupMapClickHandler();
    }
    /**
     * Set up zoom handler to show/hide markers
     * @private
     */ _setupZoomHandler() {
        const map = this.mapManager.getMap();
        if (!map) return;
        // Listen for zoom changes
        this.zoomHandler = ()=>{
            const currentZoom = map.getZoom();
            this.toggleMarkersVisibility(currentZoom);
        };
        map.on('zoom', this.zoomHandler);
    }
    /**
     * Set up map click handler to deactivate markers
     * @private
     */ _setupMapClickHandler() {
        const map = this.mapManager.getMap();
        if (!map) return;
        map.on('click', (e)=>{
            // Only deactivate if clicking on the map, not on a marker
            const clickedOnMarker = e.originalEvent.target.closest('.marker');
            if (!clickedOnMarker) {
                // Deactivate project through UIManager (which also handles markers)
                if (window.uiManager && window.uiManager.activeProjectSlug) {
                    // Recenter map before deactivating (UIManager handles coordinate lookup)
                    window.uiManager.recenterMapToActiveMarker({
                        withPanelPadding: false
                    });
                    window.uiManager.deactivateProject(window.uiManager.activeProjectSlug);
                }
                // Close both panels if they're open
                if (window.uiManager) {
                    window.uiManager.closeProjectsPanel();
                    window.uiManager.closeFiltersPanel();
                }
            }
        });
    }
    /**
     * Render all markers on the map
     * @returns {number} Number of markers rendered
     */ renderMarkers() {
        const map = this.mapManager.getMap();
        if (!map) return 0;
        // Clear existing markers first
        this.clearMarkers();
        const locations = this.dataManager.allLocations;
        locations.forEach((feature)=>{
            const marker = this._createMarker(feature);
            if (marker) {
                marker.addTo(map);
                this.markers.push(marker);
            }
        });
        this.markersRendered = true;
        // Set initial visibility based on current zoom
        const currentZoom = map.getZoom();
        this.toggleMarkersVisibility(currentZoom);
        return this.markers.length;
    }
    // ! CREATE MARKER FROM CREATE MARKER
    /**
     * Create a single marker from GeoJSON feature
     * @param {Object} feature - GeoJSON feature with properties
     * @returns {mapboxgl.Marker|null} Mapbox marker instance
     * @private
     */ _createMarker(feature) {
        const { coordinates: coordinates } = feature.geometry;
        const { id: id, typologyId: typologyId, typologyName: typologyName, projectName: projectName, photoThumbnailUrl: photoThumbnailUrl, isFeatured: isFeatured, statusName: statusName } = feature.properties;
        // Determine marker type
        const markerType = this._getMarkerType(isFeatured, photoThumbnailUrl);
        // Create marker element
        const markerEl = this._createMarkerElement(typologyId, photoThumbnailUrl, markerType);
        // Store marker metadata
        markerEl.dataset.slug = id;
        markerEl.dataset.markerType = markerType;
        markerEl.dataset.projectName = projectName;
        markerEl.dataset.typologyName = typologyName || '';
        markerEl.dataset.statusName = statusName || 'Unknown';
        // Create and return Mapbox marker with bottom anchor
        const marker = new mapboxgl.Marker({
            element: markerEl,
            anchor: 'bottom'
        }).setLngLat(coordinates);
        // Add click handler
        this._setupMarkerClickHandler(markerEl, id, coordinates);
        // Add hover handlers
        this._setupMarkerHoverHandlers(markerEl, markerType);
        return marker;
    }
    /**
     * Determine marker type based on feature properties
     * @param {boolean} isFeatured - Whether project is featured
     * @param {string|null} photoThumbnailUrl - Photo URL
     * @returns {string} Marker type
     * @private
     */ _getMarkerType(isFeatured, photoThumbnailUrl) {
        if (isFeatured) return $ef2c6018bdbcf2f4$var$MarkerManager.CONFIG.MARKER_TYPES.FEATURED;
        else // PERFORMANCE: Non-featured markers don't load photos anymore
        // Only featured markers show photo thumbnails
        return $ef2c6018bdbcf2f4$var$MarkerManager.CONFIG.MARKER_TYPES.NON_FEATURED_NO_PHOTO;
    }
    // ! CREATE MARKER DOM FROM STYLING 
    /**
     * Create marker DOM element with styling
     * @param {string} typologyId - Typology ID for color lookup
     * @param {string|null} photoThumbnailUrl - Optional thumbnail URL
     * @param {string} markerType - Marker type (featured, with-photo, no-photo)
     * @returns {HTMLElement} Styled marker element
     * @private
     */ _createMarkerElement(typologyId, photoThumbnailUrl, markerType) {
        const markerEl = document.createElement('div');
        markerEl.className = 'marker';
        // Create inner circle wrapper positioned above anchor point
        const circleWrapper = document.createElement('div');
        circleWrapper.className = 'marker-circle-wrapper';
        circleWrapper.style.position = 'absolute';
        circleWrapper.style.bottom = '10px'; // 10px = 6px (line start) + 4px (gap)
        circleWrapper.style.left = '50%';
        circleWrapper.style.transform = 'translateX(-50%)';
        circleWrapper.style.pointerEvents = 'auto';
        markerEl.appendChild(circleWrapper);
        // PERFORMANCE OPTIMIZATION: Labels created on-demand during hover
        // This reduces initial DOM size significantly (3 divs vs 0 per marker)
        const color = this.dataManager.getTypologyColor(typologyId);
        // Style marker circle wrapper based on type
        // PERFORMANCE: Only load images for featured markers
        if (markerType === $ef2c6018bdbcf2f4$var$MarkerManager.CONFIG.MARKER_TYPES.FEATURED && photoThumbnailUrl && photoThumbnailUrl !== 'null') this._styleImageMarker(circleWrapper, color, photoThumbnailUrl, markerType);
        else this._styleSolidMarker(circleWrapper, color, markerType);
        return markerEl;
    }
    // ! STYLE MARKERS
    /**
     * Apply styling to image marker
     * @param {HTMLElement} element - Marker element
     * @param {string} borderColor - Border color (typology color)
     * @param {string} imageUrl - Background image URL
     * @param {string} markerType - Marker type
     * @private
     */ _styleImageMarker(element, borderColor, imageUrl, markerType) {
        const config = $ef2c6018bdbcf2f4$var$MarkerManager.CONFIG;
        const sizes = config.SIZES;
        // Determine base size
        let baseSize;
        if (markerType === config.MARKER_TYPES.FEATURED) baseSize = sizes.FEATURED_BASE;
        else baseSize = sizes.WITH_PHOTO_BASE;
        element.style.width = baseSize;
        element.style.height = baseSize;
        element.style.borderRadius = config.MARKER.BORDER_RADIUS;
        element.style.border = `${config.MARKER.BORDER_WIDTH} solid ${borderColor}`;
        element.style.backgroundImage = `url(${imageUrl})`;
        element.style.backgroundSize = 'cover';
        element.style.backgroundPosition = 'center';
        element.style.cursor = config.MARKER.CURSOR;
        element.style.zIndex = config.Z_INDEX.BASE;
        element.style.boxShadow = '0 6px 10px rgba(0, 0, 0, 0.8)';
    }
    /**
     * Apply styling to solid color marker
     * @param {HTMLElement} element - Marker element
     * @param {string} backgroundColor - Background color (typology color)
     * @param {string} markerType - Marker type
     * @private
     */ _styleSolidMarker(element, backgroundColor, markerType) {
        const config = $ef2c6018bdbcf2f4$var$MarkerManager.CONFIG;
        element.style.backgroundColor = backgroundColor;
        element.style.width = config.SIZES.NO_PHOTO_BASE;
        element.style.height = config.SIZES.NO_PHOTO_BASE;
        element.style.borderRadius = config.MARKER.BORDER_RADIUS;
        element.style.border = `${config.MARKER.BORDER_WIDTH} solid ${config.MARKER.BORDER_COLOR_DEFAULT}`;
        element.style.cursor = config.MARKER.CURSOR;
        element.style.zIndex = config.Z_INDEX.BASE;
        element.style.boxShadow = '0 6px 10px rgba(0, 0, 0, 0.6)';
    }
    /**
     * Create marker labels on-demand (performance optimization)
     * @param {HTMLElement} markerEl - Marker element
     * @returns {HTMLElement} Labels container
     * @private
     */ _createMarkerLabels(markerEl) {
        const circleWrapper = markerEl.querySelector('.marker-circle-wrapper');
        if (!circleWrapper) return null;
        // Create labels container
        const labelsContainer = document.createElement('div');
        labelsContainer.className = 'marker-labels-container';
        Object.assign(labelsContainer.style, {
            position: 'absolute',
            top: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            marginTop: '8px',
            gap: '4px',
            pointerEvents: 'auto',
            opacity: '0',
            transition: 'opacity 0.2s ease',
            zIndex: '1000'
        });
        // Create project name label
        const labelEl = document.createElement('div');
        labelEl.className = 'marker-label is--caps';
        Object.assign(labelEl.style, {
            color: 'black',
            padding: '3px',
            fontFamily: 'Twkeverett Book, Arial, sans-serif',
            fontSize: '12px',
            fontWeight: '600',
            lineHeight: '0.95',
            letterSpacing: '0.05em',
            whiteSpace: 'nowrap',
            textAlign: 'center',
            textShadow: '0 0 0.2em rgba(255,255,255,0.5),0 0 0.2em rgba(255,255,255,0.5),0 0 0.2em rgba(255,255,255,0.5),0 0 0.2em rgba(255,255,255,0.5),0 0 0.2em rgba(255,255,255,0.5),0 0 0.2em rgba(255,255,255,0.5),0 0 0.2em rgba(255,255,255,0.5),0 0 0.2em rgba(255,255,255,0.5),0 0 0.2em rgba(255,255,255,0.5), 0 0 0.2em rgba(255,255,255,0.5), 0 0 0.2em rgba(255,255,255,0.5),0 0 0.2em rgba(255,255,255,0.5),0 0 0.2em rgba(255,255,255,0.5),0 0 0.3em rgba(255,255,255,0.5),0 0 0.3em rgba(255,255,255,0.5),0 0 0.3em rgba(255,255,255,0.5),0 0 0.3em rgba(255,255,255,0.5),0 0 0.3em rgba(255,255,255,0.5),0 0 0.3em rgba(255,255,255,0.5),0 0 0.3em rgba(255,255,255,0.5),0 0 0.3em rgba(255,255,255,0.5),0 0 0.3em rgba(255,255,255,0.5),0 0 0.3em rgba(255,255,255,0.5),0 0 0.3em rgba(255,255,255,0.5),0 0 0.4em rgba(255,255,255,0.5),0 0 0.4em rgba(255,255,255,0.5),0 0 0.4em rgba(255,255,255,0.5),0 0 0.4em rgba(255,255,255,0.5),0 0 0.4em rgba(255,255,255,0.5),0 0 0.4em rgba(255,255,255,0.5),0 0 0.4em rgba(255,255,255,0.5),0 0 0.4em rgba(255,255,255,0.5)',
            margin: '0'
        });
        labelsContainer.appendChild(labelEl);
        // Create pills container
        const pillsContainer = document.createElement('div');
        pillsContainer.className = 'marker-pills-container';
        Object.assign(pillsContainer.style, {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '2px'
        });
        // pill styling
        const pillStyle = {
            color: 'black',
            padding: '2px',
            fontFamily: 'Twkeverett Book, Arial, sans-serif',
            fontSize: '10px',
            fontWeight: '400',
            lineHeight: '0.95',
            whiteSpace: 'nowrap',
            textAlign: 'center',
            textShadow: '0 0 0.2em rgba(255,255,255,0.5),0 0 0.2em rgba(255,255,255,0.5),0 0 0.2em rgba(255,255,255,0.5),0 0 0.2em rgba(255,255,255,0.5),0 0 0.2em rgba(255,255,255,0.5),0 0 0.2em rgba(255,255,255,0.5),0 0 0.2em rgba(255,255,255,0.5),0 0 0.2em rgba(255,255,255,0.5),0 0 0.2em rgba(255,255,255,0.5), 0 0 0.2em rgba(255,255,255,0.5), 0 0 0.2em rgba(255,255,255,0.5),0 0 0.2em rgba(255,255,255,0.5),0 0 0.2em rgba(255,255,255,0.5),0 0 0.3em rgba(255,255,255,0.5),0 0 0.3em rgba(255,255,255,0.5),0 0 0.3em rgba(255,255,255,0.5),0 0 0.3em rgba(255,255,255,0.5),0 0 0.3em rgba(255,255,255,0.5),0 0 0.3em rgba(255,255,255,0.5),0 0 0.3em rgba(255,255,255,0.5),0 0 0.3em rgba(255,255,255,0.5),0 0 0.3em rgba(255,255,255,0.5),0 0 0.3em rgba(255,255,255,0.5),0 0 0.3em rgba(255,255,255,0.5),0 0 0.4em rgba(255,255,255,0.5),0 0 0.4em rgba(255,255,255,0.5),0 0 0.4em rgba(255,255,255,0.5),0 0 0.4em rgba(255,255,255,0.5),0 0 0.4em rgba(255,255,255,0.5),0 0 0.4em rgba(255,255,255,0.5),0 0 0.4em rgba(255,255,255,0.5),0 0 0.4em rgba(255,255,255,0.5)',
            margin: '0'
        };
        // Create typology label
        const typologyLabel = document.createElement('div');
        typologyLabel.className = 'marker-label-typology is--caps';
        Object.assign(typologyLabel.style, pillStyle);
        pillsContainer.appendChild(typologyLabel);
        // Create status label
        const statusLabel = document.createElement('div');
        statusLabel.className = 'marker-label-status is--caps';
        Object.assign(statusLabel.style, pillStyle);
        pillsContainer.appendChild(statusLabel);
        labelsContainer.appendChild(pillsContainer);
        circleWrapper.appendChild(labelsContainer);
        return labelsContainer;
    }
    // ! MARKER INTERACTION HANDLERS
    /**
     * Progressive zoom to marker coordinates based on current zoom level
     * Mimics legacy app behavior with 3D pitch at closer zoom levels
     * @param {Array} coordinates - [lng, lat]
     * @private
     */ _flyToMarker(coordinates) {
        const map = this.mapManager.getMap();
        if (!map) return;
        const config = $ef2c6018bdbcf2f4$var$MarkerManager.CONFIG;
        const currentZoom = map.getZoom();
        // Calculate padding for sidebar offset
        const padding = this._getMarkerPadding();
        // Progressive zoom based on current zoom level (matching legacy app)
        if (currentZoom < config.ZOOM.THRESHOLD_LOW) // Far away - zoom to level 13
        map.flyTo({
            center: coordinates,
            zoom: config.ZOOM.FIRST_CLICK,
            speed: config.FLY.SPEED,
            curve: config.FLY.CURVE,
            duration: config.FLY.DURATION_FAST,
            essential: true,
            pitch: config.FLY.PITCH_FLAT,
            bearing: config.FLY.BEARING,
            padding: padding,
            easing: (t)=>1 - Math.pow(1 - t, 6)
        });
        else if (currentZoom <= config.ZOOM.THRESHOLD_MID) // Medium distance - zoom to level 17
        map.flyTo({
            center: coordinates,
            zoom: config.ZOOM.SECOND_CLICK,
            speed: config.FLY.SPEED,
            curve: config.FLY.CURVE,
            duration: config.FLY.DURATION_FAST,
            essential: true,
            pitch: config.FLY.PITCH_FLAT,
            bearing: config.FLY.BEARING,
            padding: padding,
            easing: (t)=>1 - Math.pow(1 - t, 6)
        });
        else if (currentZoom <= config.ZOOM.THRESHOLD_HIGH) // Close - zoom to level 18.5 with 3D pitch
        map.flyTo({
            center: coordinates,
            zoom: config.ZOOM.THIRD_CLICK,
            speed: config.FLY.SPEED,
            curve: config.FLY.CURVE,
            duration: config.FLY.DURATION_FAST,
            essential: true,
            pitch: config.FLY.PITCH_3D,
            bearing: config.FLY.BEARING,
            padding: padding,
            easing: (t)=>1 - Math.pow(1 - t, 6)
        });
        else // Already at max zoom - recenter with 3D view
        map.flyTo({
            center: coordinates,
            zoom: currentZoom,
            speed: config.FLY.SPEED,
            curve: config.FLY.CURVE,
            duration: config.FLY.DURATION_SLOW,
            essential: true,
            pitch: config.FLY.PITCH_3D,
            bearing: config.FLY.BEARING,
            padding: padding,
            easing: (t)=>1 - Math.pow(1 - t, 6)
        });
    }
    /**
     * Get marker padding to account for sidebar
     * @returns {Object} Padding object for flyTo - guaranteed to have numeric values
     * @private
     */ _getMarkerPadding() {
        // Default padding with all numeric values
        const defaultPadding = {
            top: 0,
            bottom: 0,
            left: 0,
            right: 0
        };
        // Check if we have uiManager available
        if (!window.uiManager) return defaultPadding;
        // Get padding from uiManager if available
        if (typeof window.uiManager.getMapPadding === 'function') {
            const padding = window.uiManager.getMapPadding();
            // Validate and ensure all values are numbers
            return {
                top: Number(padding.top) || 0,
                bottom: Number(padding.bottom) || 0,
                left: Number(padding.left) || 0,
                right: Number(padding.right) || 0
            };
        }
        return defaultPadding;
    }
    /**
     * Setup click handler for marker
     * @param {HTMLElement} markerEl - Marker element
     * @param {string} slug - Project slug
     * @param {Array} coordinates - [lng, lat]
     * @private
     */ _setupMarkerClickHandler(markerEl, slug, coordinates) {
        markerEl.addEventListener('click', (e)=>{
            e.stopPropagation();
            const map = this.mapManager.getMap();
            if (!map) return;
            // Check if this marker is already active
            const isAlreadyActive = this.activeMarkerSlug === slug;
            if (!isAlreadyActive) {
                // First click - activate and zoom progressively
                this._flyToMarker(coordinates);
                // Activate marker
                this.activateMarker(markerEl, slug);
                // Open projects panel and activate project
                if (window.uiManager) {
                    window.uiManager.openProjectsPanel();
                    window.uiManager.activateProject(slug);
                }
            } else // Clicking already active marker - zoom in further with 3D view
            this._flyToMarker(coordinates);
        });
    }
    /**
     * Setup hover handlers for marker
     * @param {HTMLElement} markerEl - Marker element
     * @param {string} markerType - Marker type
     * @private
     */ _setupMarkerHoverHandlers(markerEl, markerType) {
        const config = $ef2c6018bdbcf2f4$var$MarkerManager.CONFIG;
        markerEl.addEventListener('mouseenter', ()=>{
            // If marker is already active with top z-index, don't change anything
            const isActive = markerEl.classList.contains('marker-active');
            if (isActive && markerEl.style.zIndex == config.Z_INDEX.ACTIVE) return;
            // PERFORMANCE: Create labels on-demand
            let labelsContainer = markerEl.querySelector('.marker-labels-container');
            if (!labelsContainer) labelsContainer = this._createMarkerLabels(markerEl);
            // Show labels container and populate labels
            if (labelsContainer) {
                labelsContainer.style.opacity = '1';
                const labelEl = labelsContainer.querySelector('.marker-label');
                if (labelEl && markerEl.dataset.projectName) labelEl.textContent = markerEl.dataset.projectName;
                const typologyLabel = labelsContainer.querySelector('.marker-label-typology');
                if (typologyLabel && markerEl.dataset.typologyName) typologyLabel.textContent = markerEl.dataset.typologyName;
                const statusLabel = labelsContainer.querySelector('.marker-label-status');
                if (statusLabel && markerEl.dataset.statusName) statusLabel.textContent = markerEl.dataset.statusName;
            }
            // Set hover z-index (always on top)
            markerEl.style.zIndex = config.Z_INDEX.HOVER;
            // Scale up the circle wrapper based on marker type
            const circleWrapper = markerEl.querySelector('.marker-circle-wrapper');
            if (circleWrapper) {
                let hoverSize;
                if (markerType === config.MARKER_TYPES.FEATURED) hoverSize = config.SIZES.FEATURED_HOVER;
                else if (markerType === config.MARKER_TYPES.NON_FEATURED_WITH_PHOTO) hoverSize = config.SIZES.WITH_PHOTO_HOVER;
                else hoverSize = config.SIZES.NO_PHOTO_HOVER;
                circleWrapper.style.width = hoverSize;
                circleWrapper.style.height = hoverSize;
            }
        });
        markerEl.addEventListener('mouseleave', ()=>{
            // Check if marker is active
            const isActive = markerEl.classList.contains('marker-active');
            // If marker is active (clicked), keep it at active size and z-index
            if (isActive) {
                // Keep label visible for active marker
                markerEl.style.zIndex = config.Z_INDEX.ACTIVE;
                // Keep active size on circle wrapper
                const circleWrapper = markerEl.querySelector('.marker-circle-wrapper');
                if (circleWrapper) {
                    let activeSize;
                    if (markerType === config.MARKER_TYPES.FEATURED) activeSize = config.SIZES.FEATURED_HOVER;
                    else if (markerType === config.MARKER_TYPES.NON_FEATURED_WITH_PHOTO) activeSize = config.SIZES.WITH_PHOTO_HOVER;
                    else activeSize = config.SIZES.NO_PHOTO_HOVER;
                    circleWrapper.style.width = activeSize;
                    circleWrapper.style.height = activeSize;
                }
            } else {
                // Hide labels container for non-active markers
                const labelsContainer = markerEl.querySelector('.marker-labels-container');
                if (labelsContainer) labelsContainer.style.opacity = '0';
                // Reset z-index and size for non-active markers
                markerEl.style.zIndex = config.Z_INDEX.BASE;
                const circleWrapper = markerEl.querySelector('.marker-circle-wrapper');
                if (circleWrapper) {
                    let baseSize;
                    if (markerType === config.MARKER_TYPES.FEATURED) baseSize = config.SIZES.FEATURED_BASE;
                    else if (markerType === config.MARKER_TYPES.NON_FEATURED_WITH_PHOTO) baseSize = config.SIZES.WITH_PHOTO_BASE;
                    else baseSize = config.SIZES.NO_PHOTO_BASE;
                    circleWrapper.style.width = baseSize;
                    circleWrapper.style.height = baseSize;
                }
            }
        });
    }
    /**
     * Activate a marker
     * @param {HTMLElement} markerEl - Marker element
     * @param {string} slug - Project slug
     * @public
     */ activateMarker(markerEl, slug) {
        // Deactivate previous marker
        if (this.activeMarker && this.activeMarker !== markerEl) this.deactivateMarker(this.activeMarker);
        const markerType = markerEl.dataset.markerType;
        const config = $ef2c6018bdbcf2f4$var$MarkerManager.CONFIG;
        // PERFORMANCE: Create labels on-demand if not exist
        let labelsContainer = markerEl.querySelector('.marker-labels-container');
        if (!labelsContainer) labelsContainer = this._createMarkerLabels(markerEl);
        // Show labels container and populate labels
        if (labelsContainer) {
            labelsContainer.style.opacity = '1';
            const labelEl = labelsContainer.querySelector('.marker-label');
            if (labelEl && markerEl.dataset.projectName) labelEl.textContent = markerEl.dataset.projectName;
            const typologyLabel = labelsContainer.querySelector('.marker-label-typology');
            if (typologyLabel && markerEl.dataset.typologyName) typologyLabel.textContent = markerEl.dataset.typologyName;
            const statusLabel = labelsContainer.querySelector('.marker-label-status');
            if (statusLabel && markerEl.dataset.statusName) statusLabel.textContent = markerEl.dataset.statusName;
        }
        // Add active class
        markerEl.classList.add('marker-active');
        // Set active z-index
        markerEl.style.zIndex = config.Z_INDEX.ACTIVE;
        // Scale circle wrapper to hover size when active
        const circleWrapper = markerEl.querySelector('.marker-circle-wrapper');
        if (circleWrapper) {
            let activeSize;
            if (markerType === config.MARKER_TYPES.FEATURED) activeSize = config.SIZES.FEATURED_HOVER; // 60px (1.5x)
            else if (markerType === config.MARKER_TYPES.NON_FEATURED_WITH_PHOTO) activeSize = config.SIZES.WITH_PHOTO_HOVER; // 50px
            else activeSize = config.SIZES.NO_PHOTO_HOVER; // 18px (1.5x)
            circleWrapper.style.width = activeSize;
            circleWrapper.style.height = activeSize;
        }
        // Store active marker
        this.activeMarker = markerEl;
        this.activeMarkerSlug = slug;
    }
    /**
     * Deactivate a marker
     * @param {HTMLElement} markerEl - Marker element
     * @public
     */ deactivateMarker(markerEl) {
        if (!markerEl) return;
        const markerType = markerEl.dataset.markerType;
        const config = $ef2c6018bdbcf2f4$var$MarkerManager.CONFIG;
        const labelsContainer = markerEl.querySelector('.marker-labels-container');
        // Hide labels container
        if (labelsContainer) labelsContainer.style.opacity = '0';
        // Remove active class
        markerEl.classList.remove('marker-active');
        // Reset z-index
        markerEl.style.zIndex = config.Z_INDEX.BASE;
        // Reset circle wrapper to base size
        const circleWrapper = markerEl.querySelector('.marker-circle-wrapper');
        if (circleWrapper) {
            let baseSize;
            if (markerType === config.MARKER_TYPES.FEATURED) baseSize = config.SIZES.FEATURED_BASE;
            else if (markerType === config.MARKER_TYPES.NON_FEATURED_WITH_PHOTO) baseSize = config.SIZES.WITH_PHOTO_BASE;
            else baseSize = config.SIZES.NO_PHOTO_BASE;
            circleWrapper.style.width = baseSize;
            circleWrapper.style.height = baseSize;
        }
    }
    /**
     * Deactivate all markers
     * @param {boolean} skipUISync - Skip UI synchronization to prevent circular calls
     * @public
     */ deactivateAllMarkers(skipUISync = false) {
        if (this.activeMarker) {
            this.deactivateMarker(this.activeMarker);
            const wasActiveSlug = this.activeMarkerSlug;
            this.activeMarker = null;
            this.activeMarkerSlug = null;
            // Deactivate project in UI (only if not called from UI)
            if (!skipUISync && window.uiManager && wasActiveSlug) {
                window.uiManager._deactivateProjectElement(document.getElementById(wasActiveSlug));
                window.uiManager.activeProjectSlug = null;
            }
        }
    }
    /**
     * Activate marker by project slug and fly to it
     * @param {string} slug - Project slug
     * @public
     */ activateMarkerBySlug(slug, options = {}) {
        // Find the Mapbox marker object that has a matching slug
        const mapboxMarker = this.markers.find((marker)=>{
            const element = marker.getElement();
            return element && element.dataset.slug === slug;
        });
        if (mapboxMarker) {
            const markerEl = mapboxMarker.getElement();
            const lngLat = mapboxMarker.getLngLat();
            // If this is initial load from URL, set mid-zoom without flying
            if (options.isInitialLoad) {
                const map = this.mapManager.getMap();
                if (map) {
                    // Set zoom to a mid-level (11) to show the marker region without flying
                    map.setZoom(15);
                    map.setPitch(0);
                    map.setCenter([
                        lngLat.lng,
                        lngLat.lat
                    ]);
                }
            } else // Normal behavior - fly to the marker location using progressive zoom
            this._flyToMarker([
                lngLat.lng,
                lngLat.lat
            ]);
            // Activate the marker
            this.activateMarker(markerEl, slug);
        }
    }
    // ! CLEAR
    /**
     * Clear all markers from the map
     */ clearMarkers() {
        if (this.markers.length === 0) return;
        this.markers.forEach((marker)=>{
            marker.remove();
        });
        this.markers = [];
        this.markersRendered = false;
    }
    // ! UPDATE MARKERS (FOR FILTERING)
    /**
     * Update markers based on filtered data
     * @param {Array} filteredLocations - Filtered GeoJSON features
     */ updateMarkers(filteredLocations) {
        const map = this.mapManager.getMap();
        if (!map) return;
        // Clear existing markers
        this.clearMarkers();
        filteredLocations.forEach((feature)=>{
            const marker = this._createMarker(feature);
            if (marker) {
                marker.addTo(map);
                this.markers.push(marker);
            }
        });
        // Set visibility based on current zoom
        const currentZoom = map.getZoom();
        this.toggleMarkersVisibility(currentZoom);
    }
    /**
     * Toggle marker visibility based on zoom level
     * Markers only visible at zoom >= MIN_ZOOM (11)
     * @param {number} zoom - Current zoom level
     */ toggleMarkersVisibility(zoom) {
        if (this.markers.length === 0) return;
        const shouldShow = zoom >= $ef2c6018bdbcf2f4$var$MarkerManager.CONFIG.MIN_ZOOM;
        const display = shouldShow ? 'block' : 'none';
        this.markers.forEach((marker)=>{
            const element = marker.getElement();
            if (element) element.style.display = display;
        });
    }
    /**
     * Get specific marker by ID
     * @param {string} locationId - Location ID
     * @returns {mapboxgl.Marker|null} Marker instance or null
     */ getMarkerById(locationId) {
        return null;
    }
    // ! FLY-TO-MARKER
    /**
     * Fly to specific marker
     * @param {string} locationId - Location ID to fly to
     */ flyToMarker(locationId) {
        // Find the location in data
        const location = this.dataManager.allLocations.find((loc)=>loc.properties.id === locationId);
        if (!location) return;
        const [lng, lat] = location.geometry.coordinates;
        // ! Use MapManager to fly to location
        this.mapManager.flyTo({
            center: {
                lng: lng,
                lat: lat
            },
            zoom: 12
        });
    }
    // ! GET-MARKER-COUNT
    /**
     * Get total number of rendered markers
     * @returns {number} Number of markers
     */ getMarkerCount() {
        return this.markers.length;
    }
    // ! ARE-MARKERS-RENDERED
    /**
     * Check if markers have been rendered
     * @returns {boolean} True if markers are rendered
     */ areMarkersRendered() {
        return this.markersRendered;
    }
    /**
     * Update marker styling configuration
     * @param {Object} config - New configuration to merge
     */ updateConfig(config) {
        Object.assign($ef2c6018bdbcf2f4$var$MarkerManager.CONFIG, config);
    }
}
var // ! EXPORT
$ef2c6018bdbcf2f4$export$2e2bcd8739ae039 = $ef2c6018bdbcf2f4$var$MarkerManager;
// Legacy support for non-module environments
if (typeof window !== 'undefined') window.MarkerManager = $ef2c6018bdbcf2f4$var$MarkerManager;


/**
 * ClusterHeatManager - Handles Mapbox GL clustering and heatmap layers
 * Optimized with separate lightweight datasets for better performance
 */ class $3ff4ad7e21a96b8d$var$ClusterHeatManager {
    static CONFIG = {
        // Zoom thresholds
        CLUSTER_MAX_ZOOM: 11,
        HEATMAP_MAX_ZOOM: 11.25,
        MARKER_MIN_ZOOM: 11,
        // Cluster configuration
        CLUSTER: {
            RADIUS: 50,
            MIN_POINTS: 3,
            CIRCLE_COLOR: '#C7EEC8',
            CIRCLE_BLUR: 0.5,
            CIRCLE_OPACITY: 0.1,
            CIRCLE_RADIUS: 40
        },
        // Cluster count styling
        CLUSTER_COUNT: {
            TEXT_FONT: [
                'TWK Everett Book',
                'Arial Unicode MS Regular'
            ],
            TEXT_COLOR: '#ffffff',
            HALO_COLOR: '#505050',
            HALO_WIDTH: 3,
            HALO_BLUR: 1,
            SIZE_STEPS: [
                {
                    threshold: 20,
                    size: 16
                },
                {
                    threshold: 40,
                    size: 18
                },
                {
                    threshold: 60,
                    size: 20
                },
                {
                    threshold: 80,
                    size: 24
                }
            ]
        },
        // Heatmap styling
        HEATMAP: {
            COLOR_STOPS: [
                [
                    0,
                    'rgba(0, 0, 255, 0)'
                ],
                [
                    0.2,
                    'rgba(224, 90, 37, 0.40)'
                ],
                [
                    0.4,
                    'rgba(224, 90, 37, 0.55)'
                ],
                [
                    0.6,
                    'rgba(224, 90, 37, 0.60)'
                ],
                [
                    0.8,
                    'rgba(224, 90, 37, 0.8)'
                ],
                [
                    1,
                    'rgba(252, 212, 132, 1)'
                ]
            ],
            RADIUS: {
                MIN_ZOOM: 0,
                MIN_RADIUS: 25,
                MAX_ZOOM: 8,
                MAX_RADIUS: 40
            },
            WEIGHT: 1,
            INTENSITY: {
                MIN_ZOOM: 0,
                MIN_INTENSITY: 1,
                MAX_ZOOM: 12,
                MAX_INTENSITY: 2.5
            },
            OPACITY: {
                STOPS: [
                    [
                        0,
                        1
                    ],
                    [
                        5,
                        1
                    ],
                    [
                        10.5,
                        0.9
                    ],
                    [
                        12,
                        0
                    ]
                ]
            }
        },
        // Fly animation for cluster clicks
        FLY_ANIMATION: {
            SPEED: 0.125,
            CURVE: 2,
            DURATION: 3000,
            TARGET_ZOOM_LOW: 9,
            TARGET_ZOOM_HIGH: 12.5,
            ZOOM_THRESHOLD: 8,
            BEARING: 0,
            PITCH: 0
        }
    };
    /**
     * Create ClusterHeatManager instance
     * @param {Object} mapManager - MapManager instance
     * @param {Object} dataManager - DataManager instance
     */ constructor(mapManager, dataManager){
        if (!mapManager || !dataManager) throw new Error('ClusterHeatManager requires both mapManager and dataManager');
        this.mapManager = mapManager;
        this.dataManager = dataManager;
        // State
        this.clustersInitialized = false;
        this.heatmapInitialized = false;
        // Source IDs
        this.CLUSTER_SOURCE_ID = 'locations-for-clusters';
        this.HEATMAP_SOURCE_ID = 'locations-for-heatmap';
        // Layer IDs
        this.CLUSTER_LAYER_ID = 'locations-cluster';
        this.CLUSTER_COUNT_LAYER_ID = 'locations-cluster-count';
        this.HEATMAP_LAYER_ID = 'locations-heatmap';
    }
    // ! INITIALIZE CLUSTER + HEATMAP
    /**
     * Initialize both clustering and heatmap layers
     * Call this after map is loaded
     */ initialize() {
        this.addClusterLayers();
        this.addHeatmapLayer();
    }
    // ! ADD CLUSTER LAYER
    /**
     * Add clustering layers to the map
     * Includes circle layer and count text layer
     */ addClusterLayers() {
        const map = this.mapManager.getMap();
        if (!map) return;
        // Check if source already exists
        if (map.getSource(this.CLUSTER_SOURCE_ID)) {
            this._updateClusterSource();
            return;
        }
        // Create lightweight GeoJSON for clustering (coordinates only)
        const clusterGeoJSON = this._createLightweightGeoJSON();
        // Add cluster data source
        map.addSource(this.CLUSTER_SOURCE_ID, {
            type: 'geojson',
            data: clusterGeoJSON,
            cluster: true,
            clusterMaxZoom: $3ff4ad7e21a96b8d$var$ClusterHeatManager.CONFIG.CLUSTER_MAX_ZOOM,
            clusterRadius: $3ff4ad7e21a96b8d$var$ClusterHeatManager.CONFIG.CLUSTER.RADIUS,
            clusterMinPoints: $3ff4ad7e21a96b8d$var$ClusterHeatManager.CONFIG.CLUSTER.MIN_POINTS
        });
        // Add cluster circle layer
        this._addClusterCircleLayer(map);
        // Add cluster count text layer
        this._addClusterCountLayer(map);
        // Add click interaction
        this._setupClusterClickHandler(map);
        this.clustersInitialized = true;
    }
    /**
     * Add cluster circle layer (visual circles)
     * @private
     */ _addClusterCircleLayer(map) {
        const config = $3ff4ad7e21a96b8d$var$ClusterHeatManager.CONFIG.CLUSTER;
        map.addLayer({
            id: this.CLUSTER_LAYER_ID,
            type: 'circle',
            source: this.CLUSTER_SOURCE_ID,
            maxzoom: $3ff4ad7e21a96b8d$var$ClusterHeatManager.CONFIG.CLUSTER_MAX_ZOOM,
            paint: {
                'circle-color': config.CIRCLE_COLOR,
                'circle-blur': config.CIRCLE_BLUR,
                'circle-opacity': config.CIRCLE_OPACITY,
                'circle-radius': config.CIRCLE_RADIUS
            }
        });
    }
    /**
     * Add cluster count text layer (numbers)
     * @private
     */ _addClusterCountLayer(map) {
        const config = $3ff4ad7e21a96b8d$var$ClusterHeatManager.CONFIG.CLUSTER_COUNT;
        // Build text size steps for Mapbox expression
        const textSizeExpression = [
            'step',
            [
                'get',
                'point_count'
            ]
        ];
        textSizeExpression.push(18); // Base size
        config.SIZE_STEPS.forEach((step)=>{
            textSizeExpression.push(step.threshold, step.size);
        });
        map.addLayer({
            id: this.CLUSTER_COUNT_LAYER_ID,
            type: 'symbol',
            source: this.CLUSTER_SOURCE_ID,
            filter: [
                'has',
                'point_count'
            ],
            layout: {
                'text-field': '{point_count_abbreviated}',
                'text-font': config.TEXT_FONT,
                'text-size': textSizeExpression,
                'text-allow-overlap': false,
                'text-ignore-placement': true
            },
            paint: {
                'text-color': config.TEXT_COLOR,
                'text-halo-color': config.HALO_COLOR,
                'text-halo-width': config.HALO_WIDTH,
                'text-halo-blur': config.HALO_BLUR
            }
        });
    }
    // ! CLUSTER EVENT HANDLER
    /**
     * Set up click handler for clusters
     * @private
     */ _setupClusterClickHandler(map) {
        map.on('click', this.CLUSTER_LAYER_ID, (e)=>{
            const features = map.queryRenderedFeatures(e.point, {
                layers: [
                    this.CLUSTER_LAYER_ID
                ]
            });
            if (features.length === 0) return;
            const clusterId = features[0].properties.cluster_id;
            const currentZoom = map.getZoom();
            const config = $3ff4ad7e21a96b8d$var$ClusterHeatManager.CONFIG.FLY_ANIMATION;
            // Determine target zoom based on current zoom
            const targetZoom = currentZoom < config.ZOOM_THRESHOLD ? config.TARGET_ZOOM_LOW : config.TARGET_ZOOM_HIGH;
            // Get cluster expansion zoom and fly to it
            map.getSource(this.CLUSTER_SOURCE_ID).getClusterExpansionZoom(clusterId, (err, zoom)=>{
                if (err) return;
                // Fly to cluster location
                map.flyTo({
                    center: features[0].geometry.coordinates,
                    zoom: targetZoom,
                    speed: config.SPEED,
                    curve: config.CURVE,
                    duration: config.DURATION,
                    essential: true,
                    bearing: config.BEARING,
                    pitch: config.PITCH,
                    easing: (t)=>1 - Math.pow(1 - t, 5)
                });
            });
        });
        // Change cursor on hover
        map.on('mouseenter', this.CLUSTER_LAYER_ID, ()=>{
            map.getCanvas().style.cursor = 'pointer';
        });
        map.on('mouseleave', this.CLUSTER_LAYER_ID, ()=>{
            map.getCanvas().style.cursor = '';
        });
    }
    // ! ADD HEATMAP LAYER
    /**
     * Add heatmap layer to the map
     */ addHeatmapLayer() {
        const map = this.mapManager.getMap();
        if (!map) return;
        // Check if source already exists
        if (map.getSource(this.HEATMAP_SOURCE_ID)) {
            this._updateHeatmapSource();
            return;
        }
        // Create lightweight GeoJSON for heatmap
        const heatmapGeoJSON = this._createLightweightGeoJSON();
        // Add heatmap data source
        map.addSource(this.HEATMAP_SOURCE_ID, {
            type: 'geojson',
            data: heatmapGeoJSON
        });
        // Add heatmap layer with styling
        this._addHeatmapStyleLayer(map);
        this.heatmapInitialized = true;
    }
    /**
     * Add heatmap style layer
     * @private
     */ _addHeatmapStyleLayer(map) {
        const config = $3ff4ad7e21a96b8d$var$ClusterHeatManager.CONFIG.HEATMAP;
        // Build color stops for Mapbox expression
        const colorExpression = [
            'interpolate',
            [
                'linear'
            ],
            [
                'heatmap-density'
            ]
        ];
        config.COLOR_STOPS.forEach(([density, color])=>{
            colorExpression.push(density, color);
        });
        // Build opacity stops
        const opacityExpression = [
            'interpolate',
            [
                'linear'
            ],
            [
                'zoom'
            ]
        ];
        config.OPACITY.STOPS.forEach(([zoom, opacity])=>{
            opacityExpression.push(zoom, opacity);
        });
        map.addLayer({
            id: this.HEATMAP_LAYER_ID,
            type: 'heatmap',
            source: this.HEATMAP_SOURCE_ID,
            maxzoom: $3ff4ad7e21a96b8d$var$ClusterHeatManager.CONFIG.HEATMAP_MAX_ZOOM,
            paint: {
                'heatmap-color': colorExpression,
                'heatmap-radius': [
                    'interpolate',
                    [
                        'exponential',
                        1.5
                    ],
                    [
                        'zoom'
                    ],
                    config.RADIUS.MIN_ZOOM,
                    config.RADIUS.MIN_RADIUS,
                    config.RADIUS.MAX_ZOOM,
                    config.RADIUS.MAX_RADIUS
                ],
                'heatmap-weight': config.WEIGHT,
                'heatmap-intensity': [
                    'interpolate',
                    [
                        'linear'
                    ],
                    [
                        'zoom'
                    ],
                    config.INTENSITY.MIN_ZOOM,
                    config.INTENSITY.MIN_INTENSITY,
                    config.INTENSITY.MAX_ZOOM,
                    config.INTENSITY.MAX_INTENSITY
                ],
                'heatmap-opacity': opacityExpression
            },
            slot: 'top'
        });
    }
    /**
     * Create lightweight GeoJSON (coordinates only, no heavy properties)
     * OPTIMIZATION: Separate dataset for clustering vs marker rendering
     * @private
     */ _createLightweightGeoJSON() {
        return {
            type: 'FeatureCollection',
            features: this.dataManager.allLocations.map((feature)=>({
                    type: 'Feature',
                    geometry: {
                        type: 'Point',
                        coordinates: feature.geometry.coordinates
                    },
                    properties: {
                        // Only essential properties for clustering
                        id: feature.properties.id
                    }
                }))
        };
    }
    // ! UPDATE FOR FILTERING
    /**
     * Update cluster source with new data (after filtering)
     */ updateClusters(filteredLocations) {
        const map = this.mapManager.getMap();
        if (!map || !map.getSource(this.CLUSTER_SOURCE_ID)) return;
        const clusterGeoJSON = {
            type: 'FeatureCollection',
            features: filteredLocations.map((feature)=>({
                    type: 'Feature',
                    geometry: {
                        type: 'Point',
                        coordinates: feature.geometry.coordinates
                    },
                    properties: {
                        id: feature.properties.id
                    }
                }))
        };
        map.getSource(this.CLUSTER_SOURCE_ID).setData(clusterGeoJSON);
    }
    /**
     * Update heatmap source with new data (after filtering)
     */ updateHeatmap(filteredLocations) {
        const map = this.mapManager.getMap();
        if (!map || !map.getSource(this.HEATMAP_SOURCE_ID)) return;
        const heatmapGeoJSON = {
            type: 'FeatureCollection',
            features: filteredLocations.map((feature)=>({
                    type: 'Feature',
                    geometry: {
                        type: 'Point',
                        coordinates: feature.geometry.coordinates
                    },
                    properties: {
                        id: feature.properties.id
                    }
                }))
        };
        map.getSource(this.HEATMAP_SOURCE_ID).setData(heatmapGeoJSON);
    }
    /**
     * Update both clusters and heatmap together
     * @param {Array} filteredLocations - Filtered GeoJSON features
     */ update(filteredLocations) {
        this.updateClusters(filteredLocations);
        this.updateHeatmap(filteredLocations);
    }
    /**
     * Update cluster source (private helper)
     * @private
     */ _updateClusterSource() {
        const clusterGeoJSON = this._createLightweightGeoJSON();
        const map = this.mapManager.getMap();
        map.getSource(this.CLUSTER_SOURCE_ID).setData(clusterGeoJSON);
    }
    /**
     * Update heatmap source (private helper)
     * @private
     */ _updateHeatmapSource() {
        const heatmapGeoJSON = this._createLightweightGeoJSON();
        const map = this.mapManager.getMap();
        map.getSource(this.HEATMAP_SOURCE_ID).setData(heatmapGeoJSON);
    }
    // ! UPDATE VISIBILITY ON ZOOM
    /**
     * Show or hide cluster layers based on zoom
     * @param {number} zoom - Current zoom level
     */ toggleClustersVisibility(zoom) {
        const map = this.mapManager.getMap();
        if (!map) return;
        const visible = zoom < $3ff4ad7e21a96b8d$var$ClusterHeatManager.CONFIG.CLUSTER_MAX_ZOOM;
        const visibility = visible ? 'visible' : 'none';
        if (map.getLayer(this.CLUSTER_LAYER_ID)) map.setLayoutProperty(this.CLUSTER_LAYER_ID, 'visibility', visibility);
        if (map.getLayer(this.CLUSTER_COUNT_LAYER_ID)) map.setLayoutProperty(this.CLUSTER_COUNT_LAYER_ID, 'visibility', visibility);
    }
    /**
     * Show or hide heatmap layer based on zoom
     * @param {number} zoom - Current zoom level
     */ toggleHeatmapVisibility(zoom) {
        const map = this.mapManager.getMap();
        if (!map || !map.getLayer(this.HEATMAP_LAYER_ID)) return;
        const visible = zoom < $3ff4ad7e21a96b8d$var$ClusterHeatManager.CONFIG.HEATMAP_MAX_ZOOM;
        const visibility = visible ? 'visible' : 'none';
        map.setLayoutProperty(this.HEATMAP_LAYER_ID, 'visibility', visibility);
    }
    /**
     * Remove cluster layers from map
     */ removeClusters() {
        const map = this.mapManager.getMap();
        if (!map) return;
        if (map.getLayer(this.CLUSTER_COUNT_LAYER_ID)) map.removeLayer(this.CLUSTER_COUNT_LAYER_ID);
        if (map.getLayer(this.CLUSTER_LAYER_ID)) map.removeLayer(this.CLUSTER_LAYER_ID);
        if (map.getSource(this.CLUSTER_SOURCE_ID)) map.removeSource(this.CLUSTER_SOURCE_ID);
        this.clustersInitialized = false;
    }
    /**
     * Remove heatmap layer from map
     */ removeHeatmap() {
        const map = this.mapManager.getMap();
        if (!map) return;
        if (map.getLayer(this.HEATMAP_LAYER_ID)) map.removeLayer(this.HEATMAP_LAYER_ID);
        if (map.getSource(this.HEATMAP_SOURCE_ID)) map.removeSource(this.HEATMAP_SOURCE_ID);
        this.heatmapInitialized = false;
    }
    /**
     * Check if clusters are initialized
     * @returns {boolean}
     */ areClustersInitialized() {
        return this.clustersInitialized;
    }
    /**
     * Check if heatmap is initialized
     * @returns {boolean}
     */ isHeatmapInitialized() {
        return this.heatmapInitialized;
    }
    /**
     * Update configuration
     * @param {Object} config - New configuration to merge
     */ updateConfig(config) {
        Object.assign($3ff4ad7e21a96b8d$var$ClusterHeatManager.CONFIG, config);
    }
}
var // ! EXPORT
$3ff4ad7e21a96b8d$export$2e2bcd8739ae039 = $3ff4ad7e21a96b8d$var$ClusterHeatManager;
// Legacy support for non-module environments
if (typeof window !== 'undefined') window.ClusterHeatManager = $3ff4ad7e21a96b8d$var$ClusterHeatManager;


/**
 * FilterManager - Handles filtering logic and coordination
 * Manages active filters and updates all managers accordingly
 */ class $99aaed0855512ef8$var$FilterManager {
    static CONFIG = {
        DEBOUNCE_DELAY: 100,
        ALL_BUTTON_TYPOLOGY_ID: 'all-typology',
        ALL_BUTTON_STATUS_ID: 'all-status',
        CLEAR_FILTERS_BUTTON_ID: 'clear-filters',
        VISIBLE_COUNTER_ID: 'filter-count'
    };
    /**
     * Create FilterManager instance
     * @param {Object} dataManager - DataManager instance
     * @param {Object} markerManager - MarkerManager instance
     * @param {Object} clusterHeatManager - ClusterHeatManager instance
     */ constructor(dataManager, markerManager, clusterHeatManager){
        if (!dataManager || !markerManager || !clusterHeatManager) throw new Error('FilterManager requires dataManager, markerManager, and clusterHeatManager');
        this.dataManager = dataManager;
        this.markerManager = markerManager;
        this.clusterHeatManager = clusterHeatManager;
        this.activeTypologyIds = new Set(); // Active filters
        this.activeStatusIds = new Set(); // Active filters
        this.debounceTimer = null; // Debounce timer
        this.onFilterChangeCallbacks = []; // Filter change callbacks
    }
    // ! ---------------------------------------------------------------------------------------------------------------------
    /**
     * Set up DOM event listeners for filter buttons using element IDs
     * Generic method that works for both typology and status filters
     * @param {string} filterType - 'typology' or 'status'
     */ setupFilterButtons(filterType = 'typology') {
        const isTypology = filterType === 'typology';
        const slugMap = isTypology ? this.dataManager.typologySlugToIdMap : this.dataManager.statusSlugToIdMap;
        const slugs = Array.from(slugMap.keys());
        let attachedCount = 0;
        slugs.forEach((slug)=>{
            const element = document.getElementById(slug);
            if (element) {
                element.addEventListener('click', (e)=>{
                    e.preventDefault();
                    e.stopPropagation();
                    this._deactivateAllButton(filterType); // Deactivate corresponding "all" button
                    if (isTypology) this.toggleTypologyBySlug(slug);
                    else this.toggleStatusBySlug(slug);
                    element.classList.toggle('is--active'); // Toggle active class for visual feedback
                    this._checkAndActivateAllButton(filterType); // Check if we need to reactivate "all"
                });
                attachedCount++;
            }
        });
    }
    // ! Set up typology filter buttons
    setupTypologyFilters() {
        this.setupFilterButtons('typology');
    }
    // ! Set up status filter buttons
    setupStatusFilters() {
        this.setupFilterButtons('status');
    }
    // ! ---------------------------------------------------------------------------------------------------------------------
    /**
     * ! Set up GENERIC "All" button for both filter types
     * Supports separate "all" buttons for typology and status
     * @param {string} filterType - 'typology' or 'status'
     */ setupAllButton(filterType = 'typology') {
        const buttonId = filterType === 'typology' ? $99aaed0855512ef8$var$FilterManager.CONFIG.ALL_BUTTON_TYPOLOGY_ID : $99aaed0855512ef8$var$FilterManager.CONFIG.ALL_BUTTON_STATUS_ID;
        const allButton = document.getElementById(buttonId);
        if (allButton) {
            allButton.addEventListener('click', (e)=>{
                e.preventDefault();
                e.stopPropagation();
                if (filterType === 'typology') this.clearTypologyFilters();
                else this.clearStatusFilters();
                allButton.classList.add('is--active'); // Activate this "all" button
            });
            allButton.classList.add('is--active'); // Set as active by default on initialization
        }
    }
    // ! ALL-TYPOLOGIES BUTTON - [SETUP]
    setupTypologyAllButton() {
        this.setupAllButton('typology');
    }
    // ! ALL-STATUS BUTTON - [SETUP]
    setupStatusAllButton() {
        this.setupAllButton('status');
    }
    // ! CLEAR ALL FILTER BUTTON - [SETUP]
    setupClearAllFiltersButton() {
        const clearButton = document.getElementById($99aaed0855512ef8$var$FilterManager.CONFIG.CLEAR_FILTERS_BUTTON_ID);
        if (clearButton) clearButton.addEventListener('click', (e)=>{
            e.preventDefault();
            e.stopPropagation();
            this.clearAllFilters();
        });
    }
    // ! VISIBLE COUNTER - [SETUP]
    /**
     * Set up visible item counter
     * Displays and updates the count of visible/filtered items
     */ setupVisibleCounter() {
        const counterId = $99aaed0855512ef8$var$FilterManager.CONFIG.VISIBLE_COUNTER_ID;
        // Set initial count immediately
        const counterElement = document.getElementById(counterId);
        if (counterElement) counterElement.textContent = this.dataManager.allLocations.length;
        else return;
        // Listen for filter changes to update counter automatically
        this.onFilterChange((filteredLocations)=>{
            const counterElement = document.getElementById(counterId);
            if (counterElement) counterElement.textContent = filteredLocations.length;
        });
    }
    // ! ---------------------------------------------------------------------------------------------------------------------
    // ! TYPOLOGY TOGGLE BY [SLUG]
    /**
     * Toggle typology filter by slug
     * @param {string} slug - Typology slug from DOM
     */ toggleTypologyBySlug(slug) {
        // Convert slug to ID
        const typologyId = this.dataManager.getTypologyIdFromSlug(slug);
        if (!typologyId) return;
        this.toggleTypology(typologyId);
    }
    // ! TYPOLOGY TOGGLE BY [ID]
    /**
     * Toggle typology filter by ID
     * @param {string} typologyId - Typology ID
     */ toggleTypology(typologyId) {
        if (this.activeTypologyIds.has(typologyId)) this.activeTypologyIds.delete(typologyId);
        else this.activeTypologyIds.add(typologyId);
        this._applyFiltersDebounced();
    }
    // ! TYPOLOGY ADD -- [SLUG]
    /**
     * Add typology filter by slug
     * @param {string} slug - Typology slug
     */ addTypologyBySlug(slug) {
        const typologyId = this.dataManager.getTypologyIdFromSlug(slug);
        if (typologyId) {
            this.activeTypologyIds.add(typologyId);
            this._applyFiltersDebounced();
        }
    }
    // ! TYPOLOGY REMOVE -- [SLUG]
    /**
     * Remove typology filter by slug
     * @param {string} slug - Typology slug
     */ removeTypologyBySlug(slug) {
        const typologyId = this.dataManager.getTypologyIdFromSlug(slug);
        if (typologyId) {
            this.activeTypologyIds.delete(typologyId);
            this._applyFiltersDebounced();
        }
    }
    // ! -----------------------------------------------------------------------------------------------------------------------
    /**
     * Toggle status filter by slug
     * @param {string} slug - Status slug from DOM
     */ toggleStatusBySlug(slug) {
        // Convert slug to ID
        const statusId = this.dataManager.getStatusIdFromSlug(slug);
        if (!statusId) return;
        this.toggleStatus(statusId);
    }
    /**
     * Toggle status filter by ID
     * @param {string} statusId - Status ID
     */ toggleStatus(statusId) {
        if (this.activeStatusIds.has(statusId)) this.activeStatusIds.delete(statusId);
        else this.activeStatusIds.add(statusId);
        this._applyFiltersDebounced();
    }
    /**
     * Add status filter by slug
     * @param {string} slug - Status slug
     */ addStatusBySlug(slug) {
        const statusId = this.dataManager.getStatusIdFromSlug(slug);
        if (statusId) {
            this.activeStatusIds.add(statusId);
            this._applyFiltersDebounced();
        }
    }
    /**
     * Remove status filter by slug
     * @param {string} slug - Status slug
     */ removeStatusBySlug(slug) {
        const statusId = this.dataManager.getStatusIdFromSlug(slug);
        if (statusId) {
            this.activeStatusIds.delete(statusId);
            this._applyFiltersDebounced();
        }
    }
    /**
     * Clear all filters (both typology and status)
     */ clearAllFilters() {
        this.clearTypologyFilters();
        this.clearStatusFilters(); // Reactivate both "all" buttons
        const typologyAllButton = document.getElementById($99aaed0855512ef8$var$FilterManager.CONFIG.ALL_BUTTON_TYPOLOGY_ID);
        if (typologyAllButton) typologyAllButton.classList.add('is--active');
        const statusAllButton = document.getElementById($99aaed0855512ef8$var$FilterManager.CONFIG.ALL_BUTTON_STATUS_ID);
        if (statusAllButton) statusAllButton.classList.add('is--active');
        this._applyFiltersNow();
    }
    /**
     * Clear only typology filters
     */ clearTypologyFilters() {
        this.activeTypologyIds.clear(); // Remove active classes from typology filter buttons
        const typologySlugs = Array.from(this.dataManager.typologySlugToIdMap.keys());
        typologySlugs.forEach((slug)=>{
            const element = document.getElementById(slug);
            if (element) element.classList.remove('is--active');
        });
    }
    /**
     * Clear only status filters
     */ clearStatusFilters() {
        this.activeStatusIds.clear(); // Remove active classes from status filter buttons
        const statusSlugs = Array.from(this.dataManager.statusSlugToIdMap.keys());
        statusSlugs.forEach((slug)=>{
            const element = document.getElementById(slug);
            if (element) element.classList.remove('is--active');
        });
    }
    /**
     * Deactivate the "all" button for specified filter type
     * @param {string} filterType - 'typology' or 'status'
     * @private
     */ _deactivateAllButton(filterType = 'typology') {
        const buttonId = filterType === 'typology' ? $99aaed0855512ef8$var$FilterManager.CONFIG.ALL_BUTTON_TYPOLOGY_ID : $99aaed0855512ef8$var$FilterManager.CONFIG.ALL_BUTTON_STATUS_ID;
        const allButton = document.getElementById(buttonId);
        if (allButton) allButton.classList.remove('is--active');
    }
    /**
     * Check if no filters of this type are active, and if so, activate "all" button
     * @param {string} filterType - 'typology' or 'status'
     * @private
     */ _checkAndActivateAllButton(filterType = 'typology') {
        const isTypology = filterType === 'typology';
        const hasActiveFilters = isTypology ? this.activeTypologyIds.size > 0 : this.activeStatusIds.size > 0;
        if (!hasActiveFilters) {
            const buttonId = isTypology ? $99aaed0855512ef8$var$FilterManager.CONFIG.ALL_BUTTON_TYPOLOGY_ID : $99aaed0855512ef8$var$FilterManager.CONFIG.ALL_BUTTON_STATUS_ID;
            const allButton = document.getElementById(buttonId);
            if (allButton) allButton.classList.add('is--active');
        }
    }
    /**
     * Get current filter state
     * @returns {Object} Current filters
     */ getCurrentFilters() {
        return {
            typologyIds: Array.from(this.activeTypologyIds),
            statusIds: Array.from(this.activeStatusIds)
        };
    }
    /**
     * Check if any filters are active
     * @returns {boolean}
     */ hasActiveFilters() {
        return this.activeTypologyIds.size > 0 || this.activeStatusIds.size > 0;
    }
    /**
     * Apply filters with debouncing
     * @private
     */ _applyFiltersDebounced() {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(()=>{
            this._applyFiltersNow();
        }, $99aaed0855512ef8$var$FilterManager.CONFIG.DEBOUNCE_DELAY);
    }
    /**
     * Apply filters immediately
     * @private
     */ _applyFiltersNow() {
        const filters = this.getCurrentFilters(); // Get filtered data from DataManager
        const filteredLocations = this.dataManager.filterLocations(filters); // Update all visual components
        this.markerManager.updateMarkers(filteredLocations);
        this.clusterHeatManager.update(filteredLocations); // Trigger callbacks
        this._triggerFilterChangeCallbacks(filteredLocations);
    }
    /**
     * Register callback for filter changes
     * @param {Function} callback - Callback function (receives filteredLocations)
     */ onFilterChange(callback) {
        if (typeof callback === 'function') this.onFilterChangeCallbacks.push(callback);
    }
    /**
     * Trigger all filter change callbacks
     * @private
     */ _triggerFilterChangeCallbacks(filteredLocations) {
        this.onFilterChangeCallbacks.forEach((callback)=>{
            try {
                callback(filteredLocations);
            } catch (error) {
                console.error("\u274C Error in filter change callback:", error);
            }
        });
    }
    /**
     * Get count of visible items
     * @returns {number}
     */ getVisibleCount() {
        const filters = this.getCurrentFilters();
        const filteredLocations = this.dataManager.filterLocations(filters);
        return filteredLocations.length;
    }
}
var // ! EXPORT
$99aaed0855512ef8$export$2e2bcd8739ae039 = $99aaed0855512ef8$var$FilterManager;
// Legacy support for non-module environments
if (typeof window !== 'undefined') window.FilterManager = $99aaed0855512ef8$var$FilterManager;


/**
 * UIManager - Handles all UI interactions, panels, and button states
 * Coordinates between map interactions and UI elements
 */ class $b66ec90814aa65b3$var$UIManager {
    static CONFIG = {
        // Panel IDs
        PROJECTS_PANEL_ID: 'projects-panel',
        FILTERS_PANEL_ID: 'filters-panel',
        // Toggle button IDs
        TOGGLE_PROJECTS_BTN_ID: 'toggle-projects-panel',
        TOGGLE_FILTERS_BTN_ID: 'toggle-filters-panel',
        TOGGLE_FILTERS_BTN_ALT_ID: 'toggle-filters-panel-alt',
        // Close button IDs
        CLOSE_PROJECTS_BTN_ID: 'close-projects-panel',
        CLOSE_FILTERS_BTN_ID: 'close-filters-panel',
        // Scroll button IDs
        SCROLL_DOWN_BTN_ID: 'scroll-to--down',
        SCROLL_UP_BTN_ID: 'scroll-to--up',
        // Map control button IDs
        MAP_ZOOM_IN_BTN_ID: 'map-zoom-in',
        MAP_ZOOM_OUT_BTN_ID: 'map-zoom-out',
        MAP_ORIENTATION_BTN_ID: 'map-orientation',
        RESET_MAP_VIEW_BTN_ID: 'resetMapView',
        // Content container
        CONTENT_WRAPPER_CLASS: 'content-wrapper',
        // Panel dimensions (in pixels)
        PROJECTS_PANEL_WIDTH: 500,
        // CSS Classes
        ACTIVE_CLASS: 'is--active',
        HIDDEN_CLASS: 'is--hidden'
    };
    constructor(mapManager, markerManager, dataManager){
        if (!mapManager || !markerManager || !dataManager) throw new Error('UIManager requires mapManager, markerManager, and dataManager');
        this.mapManager = mapManager;
        this.markerManager = markerManager;
        this.dataManager = dataManager;
        this.activeProjectSlug = null;
        this.isPanelInitialized = false;
        this.filterManager = null; // Will be set after FilterManager is created
        // Reactive mobile detection
        this.isMobile = false;
        this._setupMobileMediaQuery();
    }
    /**
     * Setup reactive mobile detection with MediaQuery listener
     * Automatically updates this.isMobile when window is resized
     * @private
     */ _setupMobileMediaQuery() {
        const mediaQuery = window.matchMedia('(max-width: 767px)');
        this.isMobile = mediaQuery.matches;
        // Listen for breakpoint changes
        mediaQuery.addEventListener('change', (e)=>{
            this.isMobile = e.matches;
        });
    }
    /**
     * Set FilterManager reference and register callbacks
     * Called after FilterManager is instantiated
     * @param {Object} filterManager - FilterManager instance
     */ setFilterManager(filterManager) {
        this.filterManager = filterManager;
        // Register callback for filter changes to update project item visibility
        if (filterManager && typeof filterManager.onFilterChange === 'function') filterManager.onFilterChange((filteredLocations)=>{
            this.updateProjectItemsVisibility(filteredLocations);
        });
    }
    /**
     * Initialize all UI interactions
     */ initialize() {
        this._initializePanelStates(); // Set initial panel states (Ensure Hidden)
        this._setupProjectsPanelToggle(); // Setup projects panel toggle buttons
        this._setupFiltersPanelToggle(); // Setup filters panel toggle buttons
        this._setupCloseButtons(); // Setup close buttons
        this._setupMapControlButtons(); // Setup map control buttons
        this._setupSectionScrollButtons(); // Setup section scroll buttons
        this._setupProjectItemClickHandlers(); // Setup project item click handlers
        this._setupPanelBackgroundClickHandler(); // Setup panel background click handler
        this._updateSectionWrapperVisibility(); // Hide empty section wrappers on initial load
        this.isPanelInitialized = true;
    }
    // ==========================================
    // PANEL INITIALIZATION
    // ==========================================
    /**
     * Set initial state: both panels hidden
     * @private
     */ _initializePanelStates() {
        const projectsPanel = document.getElementById($b66ec90814aa65b3$var$UIManager.CONFIG.PROJECTS_PANEL_ID);
        const filtersPanel = document.getElementById($b66ec90814aa65b3$var$UIManager.CONFIG.FILTERS_PANEL_ID);
        if (projectsPanel) projectsPanel.style.display = 'none';
        if (filtersPanel) filtersPanel.style.display = 'none';
    }
    // ==========================================
    // PROJECTS PANEL TOGGLE
    // ==========================================
    /**
     * Setup projects panel toggle button
     * @private
     */ _setupProjectsPanelToggle() {
        const toggleBtn = document.getElementById($b66ec90814aa65b3$var$UIManager.CONFIG.TOGGLE_PROJECTS_BTN_ID);
        if (!toggleBtn) return;
        toggleBtn.addEventListener('click', ()=>{
            const panel = document.getElementById($b66ec90814aa65b3$var$UIManager.CONFIG.PROJECTS_PANEL_ID);
            const isVisible = panel && panel.style.display !== 'none';
            if (isVisible) this.closeProjectsPanel();
            else this.openProjectsPanel();
        });
    }
    /**
     * Open projects panel
     * If there's an active marker, recenter the map to account for panel padding
     */ openProjectsPanel() {
        const panel = document.getElementById($b66ec90814aa65b3$var$UIManager.CONFIG.PROJECTS_PANEL_ID);
        const toggleBtn = document.getElementById($b66ec90814aa65b3$var$UIManager.CONFIG.TOGGLE_PROJECTS_BTN_ID);
        if (panel) {
            // On mobile, close filters panel if it's open (mutually exclusive)
            if (this.isMobile) {
                const filtersPanel = document.getElementById($b66ec90814aa65b3$var$UIManager.CONFIG.FILTERS_PANEL_ID);
                if (filtersPanel && filtersPanel.style.display !== 'none') this.closeFiltersPanel();
            }
            panel.style.display = 'flex'; // Use flex to maintain layout
            if (toggleBtn) toggleBtn.classList.add($b66ec90814aa65b3$var$UIManager.CONFIG.ACTIVE_CLASS);
            // Recenter map if there's an active marker
            this.recenterMapToActiveMarker({
                withPanelPadding: true
            });
        }
    }
    /**
     * Close projects panel (does not reset active project state)
     * If there's an active marker, recenter the map to account for removed padding
     */ closeProjectsPanel() {
        const panel = document.getElementById($b66ec90814aa65b3$var$UIManager.CONFIG.PROJECTS_PANEL_ID);
        const toggleBtn = document.getElementById($b66ec90814aa65b3$var$UIManager.CONFIG.TOGGLE_PROJECTS_BTN_ID);
        if (panel) {
            // Deactivate active project before closing panel
            if (this.activeProjectSlug) this.deactivateProject(this.activeProjectSlug);
            panel.style.display = 'none';
            if (toggleBtn) toggleBtn.classList.remove($b66ec90814aa65b3$var$UIManager.CONFIG.ACTIVE_CLASS);
            // Recenter map if there was an active marker (without padding)
            this.recenterMapToActiveMarker({
                withPanelPadding: false
            });
        }
    }
    // ==========================================
    // FILTERS PANEL TOGGLE
    // ==========================================
    /**
     * Setup filters panel toggle buttons (desktop and mobile)
     * @private
     */ _setupFiltersPanelToggle() {
        const toggleBtn = document.getElementById($b66ec90814aa65b3$var$UIManager.CONFIG.TOGGLE_FILTERS_BTN_ID);
        const toggleBtnAlt = document.getElementById($b66ec90814aa65b3$var$UIManager.CONFIG.TOGGLE_FILTERS_BTN_ALT_ID);
        // Handler function for both buttons
        const handleToggle = ()=>{
            const panel = document.getElementById($b66ec90814aa65b3$var$UIManager.CONFIG.FILTERS_PANEL_ID);
            const isVisible = panel && panel.style.display !== 'none';
            if (isVisible) this.closeFiltersPanel();
            else this.openFiltersPanel();
        };
        // Attach to desktop toggle button
        if (toggleBtn) toggleBtn.addEventListener('click', handleToggle);
        else console.warn(`\u{26A0}\u{FE0F} Filters panel toggle button not found (ID: ${$b66ec90814aa65b3$var$UIManager.CONFIG.TOGGLE_FILTERS_BTN_ID})`);
        // Attach to mobile toggle button
        if (toggleBtnAlt) toggleBtnAlt.addEventListener('click', handleToggle);
    }
    /**
     * Open filters panel
     * Panel opens at its original height (defined by CSS/Webflow)
     */ openFiltersPanel() {
        const panel = document.getElementById($b66ec90814aa65b3$var$UIManager.CONFIG.FILTERS_PANEL_ID);
        const toggleBtn = document.getElementById($b66ec90814aa65b3$var$UIManager.CONFIG.TOGGLE_FILTERS_BTN_ID);
        const toggleBtnAlt = document.getElementById($b66ec90814aa65b3$var$UIManager.CONFIG.TOGGLE_FILTERS_BTN_ALT_ID);
        if (panel) {
            // On mobile, close projects panel if it's open (mutually exclusive)
            if (this.isMobile) {
                const projectsPanel = document.getElementById($b66ec90814aa65b3$var$UIManager.CONFIG.PROJECTS_PANEL_ID);
                if (projectsPanel && projectsPanel.style.display !== 'none') this.closeProjectsPanel();
            }
            panel.style.display = 'flex'; // Use flex to maintain layout
            // Reset position and opacity in case it was dragged
            if (typeof gsap !== 'undefined') gsap.set(panel, {
                y: 0,
                opacity: 1
            });
            if (toggleBtn) toggleBtn.classList.add($b66ec90814aa65b3$var$UIManager.CONFIG.ACTIVE_CLASS);
            if (toggleBtnAlt) toggleBtnAlt.classList.add($b66ec90814aa65b3$var$UIManager.CONFIG.ACTIVE_CLASS);
        }
    }
    /**
     * Close filters panel
     */ closeFiltersPanel() {
        const panel = document.getElementById($b66ec90814aa65b3$var$UIManager.CONFIG.FILTERS_PANEL_ID);
        const toggleBtn = document.getElementById($b66ec90814aa65b3$var$UIManager.CONFIG.TOGGLE_FILTERS_BTN_ID);
        const toggleBtnAlt = document.getElementById($b66ec90814aa65b3$var$UIManager.CONFIG.TOGGLE_FILTERS_BTN_ALT_ID);
        if (panel) {
            panel.style.display = 'none';
            if (toggleBtn) toggleBtn.classList.remove($b66ec90814aa65b3$var$UIManager.CONFIG.ACTIVE_CLASS);
            if (toggleBtnAlt) toggleBtnAlt.classList.remove($b66ec90814aa65b3$var$UIManager.CONFIG.ACTIVE_CLASS);
        }
    }
    // ==========================================
    // CLOSE BUTTONS
    // ==========================================
    /**
     * Setup all close buttons
     * @private
     */ _setupCloseButtons() {
        // Close projects panel button
        const closeProjectsBtn = document.getElementById($b66ec90814aa65b3$var$UIManager.CONFIG.CLOSE_PROJECTS_BTN_ID);
        if (closeProjectsBtn) closeProjectsBtn.addEventListener('click', ()=>{
            this.closeProjectsPanel();
        });
        // Close filters panel button
        const closeFiltersBtn = document.getElementById($b66ec90814aa65b3$var$UIManager.CONFIG.CLOSE_FILTERS_BTN_ID);
        if (closeFiltersBtn) closeFiltersBtn.addEventListener('click', ()=>{
            this.closeFiltersPanel();
        });
    }
    // ==========================================
    // PROJECT ITEM VISIBILITY (FILTERING)
    // ==========================================
    /**
     * Update project item visibility based on filtered locations
     * Called by FilterManager when filters change
     * @param {Array} filteredLocations - Array of filtered location features
     * @public
     */ updateProjectItemsVisibility(filteredLocations) {
        // Get all visible project slugs from filtered locations
        const visibleSlugs = new Set(filteredLocations.map((location)=>location.properties.id));
        // Get all project items
        const projectItems = document.querySelectorAll('.projects__item');
        let hiddenCount = 0;
        let visibleCount = 0;
        projectItems.forEach((projectItem)=>{
            // Find the .project element inside the .projects__item
            const projectElement = projectItem.querySelector('.project');
            if (!projectElement) return;
            const projectSlug = projectElement.id;
            if (visibleSlugs.has(projectSlug)) {
                // Show the project item
                projectItem.style.display = '';
                visibleCount++;
            } else {
                // Hide the project item
                projectItem.style.display = 'none';
                hiddenCount++;
                // If this project was active, deactivate it
                if (this.activeProjectSlug === projectSlug) {
                    this._deactivateProjectElement(projectElement);
                    this.activeProjectSlug = null;
                    // Also deactivate marker
                    if (this.markerManager) this.markerManager.deactivateAllMarkers(true);
                }
            }
        });
        // Hide section wrappers that have no visible project items
        this._updateSectionWrapperVisibility();
    }
    /**
     * Hide/show section wrappers based on whether they contain visible project items
     * Adds 'is--hidden' class to section wrappers with no visible projects
     * @private
     */ _updateSectionWrapperVisibility() {
        // Get all section wrappers
        const sectionWrappers = document.querySelectorAll('.section__wrapper');
        let hiddenSections = 0;
        let visibleSections = 0;
        sectionWrappers.forEach((sectionWrapper)=>{
            // Find all project items within this section wrapper
            const projectItems = sectionWrapper.querySelectorAll('.projects__item');
            // Count how many are visible
            const visibleProjectsCount = Array.from(projectItems).filter((item)=>{
                return item.style.display !== 'none';
            }).length;
            // If no visible projects, hide the section wrapper
            if (visibleProjectsCount === 0) {
                sectionWrapper.classList.add('is--hidden');
                hiddenSections++;
            } else {
                sectionWrapper.classList.remove('is--hidden');
                visibleSections++;
            }
        });
    }
    // ==========================================
    // PROJECT ITEM CLICK HANDLERS
    // ==========================================
    /**
     * Setup click handler for projects panel background
     * Deactivates projects when clicking directly on the panel (not children)
     * @private
     */ _setupPanelBackgroundClickHandler() {
        const projectsPanel = document.getElementById($b66ec90814aa65b3$var$UIManager.CONFIG.PROJECTS_PANEL_ID);
        if (!projectsPanel) return;
        projectsPanel.addEventListener('click', (e)=>{
            // Only deactivate if clicking directly on the panel, not its children
            if (e.target === projectsPanel) {
                this._deactivateAllProjects();
                this.activeProjectSlug = null;
                // Deactivate corresponding marker
                if (this.markerManager && this.markerManager.deactivateAllMarkers) this.markerManager.deactivateAllMarkers(true);
            }
        });
    }
    /**
     * Setup click handlers for project items in the list
     * Uses event delegation on the content wrapper
     * @private
     */ _setupProjectItemClickHandlers() {
        const contentWrapper = document.querySelector(`.${$b66ec90814aa65b3$var$UIManager.CONFIG.CONTENT_WRAPPER_CLASS}`);
        if (!contentWrapper) return;
        // Use event delegation for better performance
        // Listen for clicks on the content wrapper container
        contentWrapper.addEventListener('click', (e)=>{
            // Find the closest project item element (.project class)
            const projectItem = e.target.closest('.project');
            if (!projectItem) return;
            // Get project slug from element ID
            const projectSlug = projectItem.id;
            if (projectSlug) this.activateProject(projectSlug);
        });
    }
    // ==========================================
    // PROJECT ACTIVATION
    // ==========================================
    /**
     * Activate a project item by expanding it inline
     * @param {string} projectSlug - The slug of the project to activate
     * @public
     */ activateProject(projectSlug, options = {}) {
        if (!projectSlug) return;
        console.log(`UI-m: Activating project: ${projectSlug}`);
        // Find the project element by ID
        const projectElement = document.getElementById(projectSlug);
        if (!projectElement) {
            console.warn(`UI-m: \u{26A0}\u{FE0F} Project element not found: ${projectSlug}`);
            return;
        }
        // Check if this project is already active
        const projectsItem = projectElement.closest('.projects__item');
        const isAlreadyActive = projectsItem?.classList.contains($b66ec90814aa65b3$var$UIManager.CONFIG.ACTIVE_CLASS);
        if (isAlreadyActive) {
            this.deactivateProject(projectSlug); // Deactivate if clicking the same project
            return;
        }
        this._deactivateAllProjects(); // Deactivate all other projects first
        this._activateProjectElement(projectElement); // Activate this project
        this._scrollToProject(projectElement); // Scroll to the project in the panel
        this.activeProjectSlug = projectSlug; // Store active project slug
        if (window.animationManager) window.animationManager.snapToActiveProjectHeight();
        if (this.markerManager && this.markerManager.activateMarkerBySlug) this.markerManager.activateMarkerBySlug(projectSlug, options);
        console.log(`UI-m: Project activated: ${projectSlug}`);
    }
    /**
     * Deactivate a specific project
     * @param {string} projectSlug - The slug of the project to deactivate
     * @public
     */ deactivateProject(projectSlug) {
        const projectElement = document.getElementById(projectSlug);
        if (!projectElement) return;
        this._deactivateProjectElement(projectElement);
        this.activeProjectSlug = null;
        // Deactivate corresponding marker (with skipUISync to prevent circular call)
        if (this.markerManager && this.markerManager.deactivateAllMarkers) this.markerManager.deactivateAllMarkers(true);
    }
    /**
     * Activate a project element (add classes, show metadata)
     * @private
     */ _activateProjectElement(projectElement) {
        // Find parent .projects__item
        const projectsItem = projectElement.closest('.projects__item');
        if (!projectsItem) return;
        // Add is--active class to .projects__item
        projectsItem.classList.add($b66ec90814aa65b3$var$UIManager.CONFIG.ACTIVE_CLASS);
        // Force column and row span to 2 (grid-column: span 2; grid-row: span 2;)
        projectsItem.style.gridColumn = 'span 2';
        projectsItem.style.gridRow = 'span 2';
        // Find and activate .project-image__wrapper
        const imageWrapper = projectElement.querySelector('.project-image__wrapper');
        if (imageWrapper) imageWrapper.classList.add($b66ec90814aa65b3$var$UIManager.CONFIG.ACTIVE_CLASS);
        // Find and scale up h3.project__name (1.25x)
        const projectName = projectElement.querySelector('h3.project__name');
        if (projectName) projectName.style.fontSize = '1.25em';
        // Hide plus button, show minus button
        const plusButton = projectElement.querySelector('[project-item-button="plus"]');
        if (plusButton) plusButton.classList.add($b66ec90814aa65b3$var$UIManager.CONFIG.HIDDEN_CLASS);
        const minusButton = projectElement.querySelector('[project-item-button="minus"]');
        if (minusButton) minusButton.classList.remove($b66ec90814aa65b3$var$UIManager.CONFIG.HIDDEN_CLASS);
        // Show metadata container
        const metadataContainer = projectElement.querySelector('.metadata__container.is--hidden');
        if (metadataContainer) metadataContainer.classList.remove('is--hidden');
    }
    /**
     * Deactivate a project element (remove classes, hide metadata)
     * @private
     */ _deactivateProjectElement(projectElement) {
        // Find parent .projects__item
        const projectsItem = projectElement.closest('.projects__item');
        if (!projectsItem) return;
        // Remove is--active class from .projects__item
        projectsItem.classList.remove($b66ec90814aa65b3$var$UIManager.CONFIG.ACTIVE_CLASS);
        // Reset grid span
        projectsItem.style.gridColumn = '';
        projectsItem.style.gridRow = '';
        // Deactivate .project-image__wrapper
        const imageWrapper = projectElement.querySelector('.project-image__wrapper');
        if (imageWrapper) imageWrapper.classList.remove($b66ec90814aa65b3$var$UIManager.CONFIG.ACTIVE_CLASS);
        // Reset h3.project__name font size
        const projectName = projectElement.querySelector('h3.project__name');
        if (projectName) projectName.style.fontSize = '';
        // Show plus button, hide minus button
        const plusButton = projectElement.querySelector('[project-item-button="plus"]');
        if (plusButton) plusButton.classList.remove($b66ec90814aa65b3$var$UIManager.CONFIG.HIDDEN_CLASS);
        const minusButton = projectElement.querySelector('[project-item-button="minus"]');
        if (minusButton) minusButton.classList.add($b66ec90814aa65b3$var$UIManager.CONFIG.HIDDEN_CLASS);
        // Hide metadata container
        const metadataContainer = projectElement.querySelector('.metadata__container');
        if (metadataContainer && !metadataContainer.classList.contains('is--hidden')) metadataContainer.classList.add('is--hidden');
    }
    /**
     * Deactivate all projects
     * @private
     */ _deactivateAllProjects() {
        const contentWrapper = document.querySelector(`.${$b66ec90814aa65b3$var$UIManager.CONFIG.CONTENT_WRAPPER_CLASS}`);
        if (!contentWrapper) return;
        const allProjectElements = contentWrapper.querySelectorAll('.project');
        allProjectElements.forEach((projectElement)=>{
            this._deactivateProjectElement(projectElement);
        });
    }
    /**
     * Scroll to a project element in the panel
     * @private
     */ _scrollToProject(projectElement) {
        if (!projectElement) return;
        // Find the scrollable container (projects panel content area)
        const projectsPanel = document.getElementById($b66ec90814aa65b3$var$UIManager.CONFIG.PROJECTS_PANEL_ID);
        if (!projectsPanel) return;
        // Get the scrollable container (might be the panel itself or a child)
        const scrollContainer = projectsPanel.querySelector('.content-wrapper') || projectsPanel;
        // Calculate the position to scroll to
        const projectsItem = projectElement.closest('.projects__item');
        if (projectsItem && scrollContainer) {
            // Scroll to element with offset
            const elementTop = projectsItem.offsetTop;
            const offset = 32;
            scrollContainer.scrollTo({
                top: elementTop - offset,
                behavior: 'instant'
            });
        }
    }
    // ==========================================
    // MAP CONTROL BUTTONS
    // ==========================================
    /**
     * Setup map control buttons (zoom, orientation, satellite)
     * @private
     */ _setupMapControlButtons() {
        // Zoom In button
        const zoomInBtn = document.getElementById($b66ec90814aa65b3$var$UIManager.CONFIG.MAP_ZOOM_IN_BTN_ID);
        if (zoomInBtn) zoomInBtn.addEventListener('click', ()=>{
            this.mapManager.zoomIn();
        });
        // Zoom Out button
        const zoomOutBtn = document.getElementById($b66ec90814aa65b3$var$UIManager.CONFIG.MAP_ZOOM_OUT_BTN_ID);
        if (zoomOutBtn) zoomOutBtn.addEventListener('click', ()=>{
            this.mapManager.zoomOut();
        });
        // Orientation Reset button
        const orientationBtn = document.getElementById($b66ec90814aa65b3$var$UIManager.CONFIG.MAP_ORIENTATION_BTN_ID);
        if (orientationBtn) orientationBtn.addEventListener('click', ()=>{
            this.mapManager.resetOrientation();
        });
        // Reset Map View / Fly Out button
        const resetViewBtn = document.getElementById($b66ec90814aa65b3$var$UIManager.CONFIG.RESET_MAP_VIEW_BTN_ID);
        if (resetViewBtn) resetViewBtn.addEventListener('click', ()=>{
            this.closeFiltersPanel(); // Close filters panel
            this.closeProjectsPanel(); // Close projects panel
            if (this.markerManager) this.markerManager.deactivateAllMarkers();
            this.mapManager.flyOut(); // Fly out with smart zoom logic
        });
    }
    // ==========================================
    // SECTION SCROLL BUTTONS
    // ==========================================
    /**
     * Setup section scroll buttons (scroll to next/previous section)
     * @private
     */ _setupSectionScrollButtons() {
        const scrollDownBtn = document.getElementById($b66ec90814aa65b3$var$UIManager.CONFIG.SCROLL_DOWN_BTN_ID);
        const scrollUpBtn = document.getElementById($b66ec90814aa65b3$var$UIManager.CONFIG.SCROLL_UP_BTN_ID);
        if (scrollDownBtn) scrollDownBtn.addEventListener('click', ()=>{
            this._scrollToNextSection('down');
        });
        if (scrollUpBtn) scrollUpBtn.addEventListener('click', ()=>{
            this._scrollToNextSection('up');
        });
    }
    /**
     * Scroll to the next section (up or down)
     * @param {string} direction - 'up' or 'down'
     * @private
     */ _scrollToNextSection(direction) {
        const projectsPanel = document.getElementById($b66ec90814aa65b3$var$UIManager.CONFIG.PROJECTS_PANEL_ID);
        if (!projectsPanel) return;
        // Get the scrollable container (content-wrapper or panel itself)
        const scrollContainer = projectsPanel.querySelector('.content-wrapper') || projectsPanel;
        // Get all section headers in the projects panel that are visible
        const sectionHeaders = Array.from(scrollContainer.querySelectorAll('.section-header__wrapper')).filter((header)=>{
            // Only include visible section headers (not hidden by filters)
            const sectionWrapper = header.closest('.section__wrapper');
            return !sectionWrapper || !sectionWrapper.classList.contains('is--hidden');
        });
        if (sectionHeaders.length === 0) return;
        // Get current scroll position and container dimensions
        const scrollTop = scrollContainer.scrollTop;
        const containerHeight = scrollContainer.clientHeight;
        const scrollHeight = scrollContainer.scrollHeight;
        const viewportTop = scrollTop;
        const viewportBottom = scrollTop + containerHeight;
        let targetSection = null;
        if (direction === 'down') {
            // Check if we're at or near the bottom
            const isAtBottom = scrollTop + containerHeight >= scrollHeight - 50; // 50px threshold
            if (isAtBottom) // We're at the bottom, wrap to first section
            targetSection = sectionHeaders[0];
            else {
                // Find the first section that is NOT currently fully in view
                for(let i = 0; i < sectionHeaders.length; i++){
                    const sectionTop = sectionHeaders[i].offsetTop;
                    // If section starts below current viewport top, it's the next one
                    if (sectionTop > viewportTop + 30) {
                        targetSection = sectionHeaders[i];
                        break;
                    }
                }
                // If no section found below, wrap to first section
                if (!targetSection && sectionHeaders.length > 0) targetSection = sectionHeaders[0];
            }
        } else if (direction === 'up') {
            // Find the previous section (not currently at top of viewport)
            for(let i = sectionHeaders.length - 1; i >= 0; i--){
                const sectionTop = sectionHeaders[i].offsetTop;
                // If section starts above current viewport top (minus margin), it's the previous one
                if (sectionTop < viewportTop - 30) {
                    targetSection = sectionHeaders[i];
                    break;
                }
            }
            // If no section found above, wrap to last section
            if (!targetSection && sectionHeaders.length > 0) targetSection = sectionHeaders[sectionHeaders.length - 1];
        }
        // Scroll to target section
        if (targetSection) {
            // Check if mobile for different offset
            const offset = this.isMobile ? 22 : 5; // 20px on mobile, 5px on desktop
            scrollContainer.scrollTo({
                top: targetSection.offsetTop - offset,
                behavior: 'smooth'
            });
        }
    }
    /**
     * Recenter map to active marker with appropriate padding
     * Centralized method to handle all recentering logic
     * @param {Object} options - Options for recentering
     * @param {boolean} options.withPanelPadding - Whether to include panel padding (default: current panel state)
     * @param {number} options.duration - Animation duration in ms (default: 750)
     * @returns {boolean} Whether recentering was performed
     */ recenterMapToActiveMarker(options = {}) {
        const { withPanelPadding: withPanelPadding = null, duration: duration = 750 } = options;
        // Check if there's an active marker
        const hasActiveMarker = this.markerManager && this.markerManager.activeMarkerSlug;
        if (!hasActiveMarker) return false; // No active marker, nothing to recenter
        // Get the active marker's coordinates
        const activeMarker = this.markerManager.markers.find((marker)=>{
            const element = marker.getElement();
            return element && element.dataset.slug === this.markerManager.activeMarkerSlug;
        });
        if (!activeMarker) return false; // Marker not found
        const lngLat = activeMarker.getLngLat();
        const activeMarkerCoordinates = [
            lngLat.lng,
            lngLat.lat
        ];
        // Get the map instance
        const map = this.mapManager.getMap();
        if (!map) return false; // Map not available
        // Determine padding and ensure all values are numbers
        let padding;
        if (withPanelPadding === null) // Auto-detect: use current panel state
        padding = this.getMapPadding();
        else if (withPanelPadding === true) // Force use panel padding
        padding = this.getMapPadding();
        else // No padding
        padding = {
            top: 0,
            bottom: 0,
            left: 0,
            right: 0
        };
        // Double-check all padding values are numbers (Mapbox requirement)
        padding = {
            top: Number(padding.top) || 0,
            bottom: Number(padding.bottom) || 0,
            left: Number(padding.left) || 0,
            right: Number(padding.right) || 0
        };
        // Smoothly recenter the map
        map.easeTo({
            center: activeMarkerCoordinates,
            duration: duration,
            padding: padding,
            easing: (t)=>t * (2 - t // easeOutQuad
                )
        });
        return true;
    }
    /**
     * Get map padding to account for open panels
     * Used by markerManager when flying to markers
     * @returns {Object} Padding object { top, bottom, left, right } - all numbers
     */ getMapPadding() {
        const panel = document.getElementById($b66ec90814aa65b3$var$UIManager.CONFIG.PROJECTS_PANEL_ID);
        if (!panel) return {
            top: 0,
            bottom: 0,
            left: 0,
            right: 0
        };
        // Check if panel is actually visible (using display style, not class)
        const panelHidden = panel.style.display === 'none';
        if (this.isMobile && !panelHidden) {
            // Mobile: Panel is at bottom - add bottom padding (panel height + 2rem offset)
            const panelHeight = panel.offsetHeight || 0;
            const emUnit = parseFloat(getComputedStyle(document.documentElement).fontSize);
            const offset = 1 * emUnit; // 1rem above panel
            const bottomPadding = Math.max(0, panelHeight + offset);
            return {
                top: 0,
                bottom: bottomPadding,
                left: 0,
                right: 0
            };
        } else if (!this.isMobile && !panelHidden) {
            // Desktop: Panel is on left side - add left padding
            const panelWidth = Number($b66ec90814aa65b3$var$UIManager.CONFIG.PROJECTS_PANEL_WIDTH) || 0;
            return {
                top: 0,
                bottom: 0,
                left: panelWidth,
                right: 0
            };
        }
        // Panel is closed - no padding needed
        return {
            top: 0,
            bottom: 0,
            left: 0,
            right: 0
        };
    }
}
var // ! EXPORT
$b66ec90814aa65b3$export$2e2bcd8739ae039 = $b66ec90814aa65b3$var$UIManager;
// Legacy support for non-module environments
if (typeof window !== 'undefined') window.UIManager = $b66ec90814aa65b3$var$UIManager;


/**
 * AnimationManager - Handles GSAP animations and mobile interactions
 * Manages drag handles, panel animations, and responsive behaviors
 */ class $ec1acc89a96a8bcd$var$AnimationManager {
    static CONFIG = {
        // Drag handle IDs
        PROJECTS_DRAG_HANDLE_ID: 'projects-drag-handle',
        FILTERS_DRAG_HANDLE_ID: 'filters-drag-handle',
        // Panel IDs
        PROJECTS_PANEL_ID: 'projects-panel',
        FILTERS_PANEL_ID: 'filters-panel',
        // Panel height settings (in em units)
        MIN_HEIGHT_EM: 20,
        SNAP_POSITION_PERCENT: 0.50,
        SNAP_POSITION_ACTIVE_PROJECT: 0.35,
        // Animation settings
        DRAG_DURATION: 0.5,
        DRAG_EASE: 'power2.out'
    };
    constructor(){
        // Reactive mobile detection
        this.isMobile = false;
        this._setupMobileMediaQuery();
        this.emUnit = parseFloat(getComputedStyle(document.documentElement).fontSize);
        // Draggable instances
        this.projectsDraggable = null;
        this.filtersDraggable = null;
        // Panel heights
        this.minHeight = $ec1acc89a96a8bcd$var$AnimationManager.CONFIG.MIN_HEIGHT_EM * this.emUnit;
        this.maxHeight = Math.max(this.minHeight, window.innerHeight);
        this.heightRange = this.maxHeight - this.minHeight;
        this.snapPosition = this.minHeight + this.heightRange * $ec1acc89a96a8bcd$var$AnimationManager.CONFIG.SNAP_POSITION_PERCENT;
        this.snapPositionActiveProject = this.minHeight + this.heightRange * $ec1acc89a96a8bcd$var$AnimationManager.CONFIG.SNAP_POSITION_ACTIVE_PROJECT;
        this.snapHeights = [
            this.minHeight,
            this.snapPosition,
            this.maxHeight
        ];
    }
    /**
     * Initialize all animations and mobile interactions
     * @public
     */ initialize() {
        // Register GSAP plugins
        if (typeof gsap !== 'undefined' && gsap.registerPlugin) gsap.registerPlugin(Draggable, InertiaPlugin);
        else return;
        // Initialize mobile features if on mobile
        if (this.isMobile) this._initializeMobileFeatures();
        // Set initial states for project items
        this.setInitialProjectItemStates();
        this.setupProjectItemHoverAnimations();
    }
    /**
     * Setup reactive mobile detection with MediaQuery listener
     * Automatically updates this.isMobile when window is resized
     * @private
     */ _setupMobileMediaQuery() {
        const mediaQuery = window.matchMedia('(max-width: 767px)');
        this.isMobile = mediaQuery.matches;
        // Listen for breakpoint changes
        mediaQuery.addEventListener('change', (e)=>{
            this.isMobile = e.matches;
            if (this.isMobile) this._initializeMobileFeatures();
            else this._destroyMobileFeatures();
        });
    }
    /**
     * Initialize mobile-specific features
     * @private
     */ _initializeMobileFeatures() {
        // Update height calculations based on current viewport
        this.maxHeight = Math.max(this.minHeight, window.innerHeight);
        this.heightRange = this.maxHeight - this.minHeight;
        this.snapPosition = this.minHeight + this.heightRange * $ec1acc89a96a8bcd$var$AnimationManager.CONFIG.SNAP_POSITION_PERCENT;
        this.snapPositionActiveProject = this.minHeight + this.heightRange * $ec1acc89a96a8bcd$var$AnimationManager.CONFIG.SNAP_POSITION_ACTIVE_PROJECT;
        this.snapHeights = [
            this.minHeight,
            this.snapPosition,
            this.maxHeight
        ];
        // Setup drag handles
        this._setupProjectsDragHandle();
        this._setupFiltersDragHandle();
    }
    /**
     * Destroy mobile features (clean up on desktop switch)
     * @private
     */ _destroyMobileFeatures() {
        // Kill draggable instances
        if (this.projectsDraggable) {
            this.projectsDraggable.kill();
            this.projectsDraggable = null;
        }
        if (this.filtersDraggable) {
            this.filtersDraggable.kill();
            this.filtersDraggable = null;
        }
        // Reset panel heights
        const projectsPanel = document.getElementById($ec1acc89a96a8bcd$var$AnimationManager.CONFIG.PROJECTS_PANEL_ID);
        const filtersPanel = document.getElementById($ec1acc89a96a8bcd$var$AnimationManager.CONFIG.FILTERS_PANEL_ID);
        if (projectsPanel) projectsPanel.style.height = '';
        if (filtersPanel) filtersPanel.style.height = '';
    }
    /**
     * Setup projects panel drag handle
     * @private
     */ _setupProjectsDragHandle() {
        const handle = document.getElementById($ec1acc89a96a8bcd$var$AnimationManager.CONFIG.PROJECTS_DRAG_HANDLE_ID);
        const panel = document.getElementById($ec1acc89a96a8bcd$var$AnimationManager.CONFIG.PROJECTS_PANEL_ID);
        if (!handle || !panel) return;
        // Initialize panel to minimum height
        gsap.set(panel, {
            height: `${this.minHeight}px`
        });
        const SNAP_THRESHOLD = 60; // Snap when within 60px of snap point
        const CLOSE_THRESHOLD = 50; // Close panel if dragged this far below minHeight
        // Capture snap heights in closure
        const snapHeights = this.snapHeights;
        const minHeight = this.minHeight;
        const maxHeight = this.maxHeight;
        const snapPosition = this.snapPosition;
        let startHeight = 0;
        let startY = 0;
        // Kill existing draggable if it exists
        if (this.projectsDraggable) this.projectsDraggable.kill();
        // Create draggable
        this.projectsDraggable = Draggable.create(panel, {
            trigger: handle,
            type: 'y',
            inertia: true,
            onPress: function(e) {
                startHeight = panel.offsetHeight;
                startY = e.clientY || e.touches[0].clientY;
            },
            onDrag: function(e) {
                const currentY = e.clientY || e.touches[0].clientY;
                const deltaY = currentY - startY;
                const newHeight = startHeight - deltaY;
                // Allow dragging below minHeight for dismiss gesture
                const clampedHeight = gsap.utils.clamp(minHeight - CLOSE_THRESHOLD, maxHeight, newHeight);
                // Set height directly without snapping during drag
                gsap.set(panel, {
                    height: `${clampedHeight}px`
                });
                gsap.set(this.target, {
                    y: 0
                });
            },
            onDragEnd: function() {
                gsap.set(this.target, {
                    y: 0
                });
                const currentHeight = panel.offsetHeight;
                // If dragged below minHeight threshold, close the panel
                if (currentHeight < minHeight) {
                    gsap.to(panel, {
                        height: 0,
                        opacity: 0,
                        duration: $ec1acc89a96a8bcd$var$AnimationManager.CONFIG.DRAG_DURATION * 0.6,
                        ease: $ec1acc89a96a8bcd$var$AnimationManager.CONFIG.DRAG_EASE,
                        onComplete: ()=>{
                            // Reset and close panel via UIManager
                            gsap.set(panel, {
                                height: `${minHeight}px`,
                                opacity: 1
                            });
                            if (window.uiManager && window.uiManager.closeProjectsPanel) window.uiManager.closeProjectsPanel();
                        }
                    });
                    return;
                }
                // Snap to closest height with animation
                // Increase threshold for maxHeight to make it easier to snap to top
                let closestHeight = snapHeights[0];
                let minDistance = Math.abs(currentHeight - snapHeights[0]);
                for(let i = 1; i < snapHeights.length; i++){
                    const distance = Math.abs(currentHeight - snapHeights[i]);
                    // Make maxHeight snap easier - increase its threshold
                    let adjustedDistance = distance;
                    if (snapHeights[i] === maxHeight) // If within 150px of maxHeight, make it much more attractive to snap to
                    {
                        if (distance < 150) adjustedDistance = distance * 0.3; // Make it 70% more attractive
                    }
                    if (adjustedDistance < minDistance) {
                        minDistance = adjustedDistance;
                        closestHeight = snapHeights[i];
                    }
                }
                gsap.to(panel, {
                    height: `${closestHeight}px`,
                    duration: $ec1acc89a96a8bcd$var$AnimationManager.CONFIG.DRAG_DURATION,
                    ease: $ec1acc89a96a8bcd$var$AnimationManager.CONFIG.DRAG_EASE
                });
            }
        })[0];
        // Handle click to toggle between heights
        handle.addEventListener('click', ()=>{
            const currentHeight = panel.offsetHeight;
            let targetHeight;
            // Define a tolerance for comparing heights (within 10px)
            const tolerance = 10;
            if (Math.abs(currentHeight - minHeight) < tolerance) // At initial height -> go to mid
            targetHeight = snapPosition;
            else if (Math.abs(currentHeight - snapPosition) < tolerance) // At mid height -> go to initial
            targetHeight = minHeight;
            else if (Math.abs(currentHeight - maxHeight) < tolerance) // At max height -> go to initial
            targetHeight = minHeight;
            else // Anywhere else -> go to mid
            targetHeight = snapPosition;
            gsap.to(panel, {
                height: `${targetHeight}px`,
                duration: $ec1acc89a96a8bcd$var$AnimationManager.CONFIG.DRAG_DURATION,
                ease: $ec1acc89a96a8bcd$var$AnimationManager.CONFIG.DRAG_EASE
            });
        });
    }
    /**
     * Setup filters panel drag handle
     * Drags panel down and reduces opacity, closes when dragged more than 12px
     * Height never changes
     * @private
     */ _setupFiltersDragHandle() {
        const handle = document.getElementById($ec1acc89a96a8bcd$var$AnimationManager.CONFIG.FILTERS_DRAG_HANDLE_ID);
        const panel = document.getElementById($ec1acc89a96a8bcd$var$AnimationManager.CONFIG.FILTERS_PANEL_ID);
        if (!handle || !panel) return;
        const CLOSE_THRESHOLD = 8; // Close when dragged more than 8px down
        let currentDragY = 0;
        // Kill existing draggable if it exists
        if (this.filtersDraggable) this.filtersDraggable.kill();
        // Create draggable (vertical position and opacity change)
        this.filtersDraggable = Draggable.create(panel, {
            trigger: handle,
            type: 'y',
            bounds: {
                minY: 0,
                maxY: 100
            },
            onDragStart: function() {
                currentDragY = 0;
            },
            onDrag: function() {
                currentDragY = this.y;
                // Only allow dragging down (positive y)
                if (currentDragY < 0) {
                    gsap.set(this.target, {
                        y: 0
                    });
                    currentDragY = 0;
                    return;
                }
                // Calculate opacity based on drag distance (0 to 100px range)
                const opacity = Math.max(0, 1 - currentDragY / 50);
                gsap.set(panel, {
                    opacity: opacity
                });
            },
            onDragEnd: function() {
                // If dragged more than 12px, close the panel
                if (currentDragY > CLOSE_THRESHOLD) // Animate panel down and fade out
                gsap.to(panel, {
                    y: 50,
                    opacity: 0,
                    duration: $ec1acc89a96a8bcd$var$AnimationManager.CONFIG.DRAG_DURATION * 0.6,
                    ease: $ec1acc89a96a8bcd$var$AnimationManager.CONFIG.DRAG_EASE,
                    onComplete: ()=>{
                        // Reset position and trigger panel close
                        gsap.set(panel, {
                            y: 0,
                            opacity: 1
                        });
                        if (window.uiManager && window.uiManager.closeFiltersPanel) window.uiManager.closeFiltersPanel();
                    }
                });
                else // Snap back to original position
                gsap.to(panel, {
                    y: 0,
                    opacity: 1,
                    duration: $ec1acc89a96a8bcd$var$AnimationManager.CONFIG.DRAG_DURATION * 0.5,
                    ease: 'power2.out'
                });
            }
        })[0];
        // Handle click to close panel (no animation, just deactivate)
        handle.addEventListener('click', ()=>{
            if (window.uiManager && window.uiManager.closeFiltersPanel) window.uiManager.closeFiltersPanel();
        });
    }
    /**
     * Animate panel to specific height
     * @param {string} panelId - Panel element ID
     * @param {number} height - Target height in pixels
     * @param {number} duration - Animation duration (optional)
     * @public
     */ animatePanelHeight(panelId, height, duration = $ec1acc89a96a8bcd$var$AnimationManager.CONFIG.DRAG_DURATION) {
        const panel = document.getElementById(panelId);
        if (!panel) return;
        gsap.to(panel, {
            height: `${height}px`,
            duration: duration,
            ease: $ec1acc89a96a8bcd$var$AnimationManager.CONFIG.DRAG_EASE
        });
    }
    /**
     * Get snap heights for external use
     * @returns {Object} Object with minHeight, snapPosition, maxHeight
     * @public
     */ getSnapHeights() {
        return {
            minHeight: this.minHeight,
            snapPosition: this.snapPosition,
            maxHeight: this.maxHeight
        };
    }
    /**
     * Check if mobile mode is active
     * @returns {boolean}
     * @public
     */ isMobileMode() {
        return this.isMobile;
    }
    /**
     * Snap projects panel to active project height
     * Called when a project is activated on mobile
     * @public
     */ snapToActiveProjectHeight() {
        if (!this.isMobile) return;
        const panel = document.getElementById($ec1acc89a96a8bcd$var$AnimationManager.CONFIG.PROJECTS_PANEL_ID);
        if (!panel) return;
        gsap.to(panel, {
            height: `${this.snapPositionActiveProject}px`,
            duration: $ec1acc89a96a8bcd$var$AnimationManager.CONFIG.DRAG_DURATION,
            ease: $ec1acc89a96a8bcd$var$AnimationManager.CONFIG.DRAG_EASE
        });
    }
    /**
     * Set initial GSAP states for project items
     * @public
     */ setInitialProjectItemStates() {
        // Query all project items
        const projectItems = document.querySelectorAll('.project');
        projectItems.forEach((item)=>{
            // Only apply to inactive items
            if (!item.classList.contains('is--active')) {
                // Typology bar
                const typologyBar = item.querySelector('.typology__bar');
                if (typologyBar) gsap.set(typologyBar, {
                    width: '0%'
                });
                // Button wrapper (plus)
                const buttonWrapper = item.querySelector('.button__wrapper[project-item-button="plus"]');
                if (buttonWrapper) gsap.set(buttonWrapper, {
                    autoAlpha: 0
                });
                // Project image
                const projectImage = item.querySelector('.project__image');
                if (projectImage) gsap.set(projectImage, {
                    scale: 1
                });
            }
        });
    }
    /**
     * Set up GSAP hover animations for project items
     * @public
     */ setupProjectItemHoverAnimations() {
        const projectItems = document.querySelectorAll('.project');
        projectItems.forEach((item)=>{
            // Only apply to inactive items
            if (!item.classList.contains('is--active')) {
                const typologyBar = item.querySelector('.typology__bar');
                const buttonWrapper = item.querySelector('.button__wrapper[project-item-button="plus"]');
                const projectImage = item.querySelector('.project__image');
                // Guard: must have all elements
                if (!typologyBar || !buttonWrapper || !projectImage) return;
                // Mouse enter
                item.addEventListener('mouseenter', ()=>{
                    if (item.classList.contains('is--active')) return; // Prevent if active
                    gsap.to(typologyBar, {
                        width: '100%',
                        duration: 0.5,
                        ease: 'power2.out'
                    });
                    gsap.to(projectImage, {
                        scale: 1.1,
                        duration: 0.5,
                        ease: 'power2.out'
                    });
                    gsap.to(buttonWrapper, {
                        autoAlpha: 1,
                        duration: 0.4,
                        ease: 'power2.out'
                    });
                });
                // Mouse leave
                item.addEventListener('mouseleave', ()=>{
                    if (item.classList.contains('is--active')) return; // Prevent if active
                    gsap.to(typologyBar, {
                        width: '0%',
                        duration: 0.5,
                        ease: 'power2.in'
                    });
                    gsap.to(projectImage, {
                        scale: 1,
                        duration: 0.5,
                        ease: 'power2.in'
                    });
                    gsap.to(buttonWrapper, {
                        autoAlpha: 0,
                        duration: 0.4,
                        ease: 'power2.in'
                    });
                });
            }
        });
    }
}
var // ! EXPORT
$ec1acc89a96a8bcd$export$2e2bcd8739ae039 = $ec1acc89a96a8bcd$var$AnimationManager;
// Legacy support for non-module environments
if (typeof window !== 'undefined') window.AnimationManager = $ec1acc89a96a8bcd$var$AnimationManager;


/**
 * URLManager - Handles URL parameters for project activation and state management
 * Manages browser URL state and coordinates project activation via URL params
 */ class $076dce5fefd24bda$var$URLManager {
    static CONFIG = {
        // URL parameter name for project slug
        URL_PARAM_PROJECT: 'project'
    };
    constructor(uiManager, markerManager, dataManager){
        if (!uiManager || !markerManager || !dataManager) throw new Error('URLManager requires uiManager, markerManager, and dataManager');
        this.uiManager = uiManager;
        this.markerManager = markerManager;
        this.dataManager = dataManager;
        this.isReady = false;
        this.isInitialLoad = true; // Track if this is the first URL check
    }
    /**
     * Initialize URL Manager
     * Sets up URL parameter checking and popstate listener for browser back/forward
     */ initialize() {
        // Check URL parameters for initial project activation
        this._checkURLParameters();
        // Listen for browser back/forward navigation
        this._setupPopStateListener();
        this.isReady = true;
    }
    /**
     * Set up popstate listener for browser back/forward buttons
     * @private
     */ _setupPopStateListener() {
        window.addEventListener('popstate', ()=>{
            this._checkURLParameters();
        });
    }
    /**
     * Check URL parameters for project activation
     * @private
     */ _checkURLParameters() {
        const urlParams = new URLSearchParams(window.location.search);
        const projectSlug = urlParams.get($076dce5fefd24bda$var$URLManager.CONFIG.URL_PARAM_PROJECT);
        if (projectSlug) // Activate the project, passing initial load flag
        this.activateProjectBySlug(projectSlug, {
            isInitialLoad: this.isInitialLoad
        });
        else // No project in URL - deactivate any active project
        this._deactivateCurrentProject();
        // After first check, subsequent checks (popstate) should allow flying
        this.isInitialLoad = false;
    }
    /**
     * Deactivate current project
     * @private
     */ _deactivateCurrentProject() {
        // Deactivate marker
        if (this.markerManager) this.markerManager.deactivateAllMarkers();
        // Deactivate project in UI
        if (this.uiManager && this.uiManager.activeProjectSlug) this.uiManager.deactivateProject(this.uiManager.activeProjectSlug);
        // Close panels
        if (this.uiManager) this.uiManager.closeProjectsPanel();
    }
    /**
     * Activate a project by slug
     * Coordinates UI and marker activation
     * @param {string} projectSlug - Project slug to activate
     * @param {Object} options - Activation options
     * @param {boolean} options.isInitialLoad - Whether this is from initial URL load
     * @public
     */ activateProjectBySlug(projectSlug, options = {}) {
        if (!projectSlug) return;
        // Validate project exists in data
        const projectExists = this.dataManager.allLocations.some((location)=>location.properties.id === projectSlug);
        if (!projectExists) {
            console.warn(`\u{26A0}\u{FE0F} Project not found: ${projectSlug}`);
            return;
        }
        // Open projects panel
        if (this.uiManager) this.uiManager.openProjectsPanel();
        // Activate project in UI (which will also activate marker)
        if (this.uiManager) this.uiManager.activateProject(projectSlug, options);
    }
    /**
     * Update URL with project slug
     * @param {string} projectSlug - Project slug to add to URL (null to remove)
     * @public
     */ updateURL(projectSlug) {
        if (projectSlug) {
            // Add project parameter to URL
            const url = new URL(window.location);
            url.searchParams.set($076dce5fefd24bda$var$URLManager.CONFIG.URL_PARAM_PROJECT, projectSlug);
            window.history.pushState({}, '', url);
        } else {
            // Remove all query parameters and reset to base URL
            const baseUrl = window.location.origin + window.location.pathname;
            window.history.pushState({}, '', baseUrl);
        }
    }
    /**
     * Hook into UIManager to update URL when projects are activated/deactivated
     * Should be called after UIManager is initialized
     * @public
     */ hookIntoUIManager() {
        if (!this.uiManager) return;
        // Store original methods
        const originalActivate = this.uiManager.activateProject.bind(this.uiManager);
        const originalDeactivate = this.uiManager.deactivateProject.bind(this.uiManager);
        // Wrap activateProject
        this.uiManager.activateProject = (projectSlug)=>{
            originalActivate(projectSlug);
            this.updateURL(projectSlug);
        };
        // Wrap deactivateProject
        this.uiManager.deactivateProject = (projectSlug)=>{
            originalDeactivate(projectSlug);
            this.updateURL(null);
        };
    }
}
var // ! EXPORT
$076dce5fefd24bda$export$2e2bcd8739ae039 = $076dce5fefd24bda$var$URLManager;
// Legacy support for non-module environments
if (typeof window !== 'undefined') window.URLManager = $076dce5fefd24bda$var$URLManager;


"use strict";
window.Webflow ||= [];
window.Webflow.push(()=>{
    // ! Initialize Managers
    const dataManager = new (0, $48378b2ee7bf424c$export$2e2bcd8739ae039)();
    const mapManager = new (0, $68b7d625d290d5d0$export$2e2bcd8739ae039)();
    const animationManager = new (0, $ec1acc89a96a8bcd$export$2e2bcd8739ae039)();
    let markerManager;
    let clusterHeatManager;
    let filterManager;
    let uiManager;
    let urlManager;
    // ! Initialize Map + Data
    async function initializeMap() {
        try {
            await dataManager.fetchData();
            dataManager.processGeoData();
            // Initialize map with token from DataManager
            await mapManager.initialize(dataManager.mapboxToken);
            // Initialize MarkerManager (needs both map and data ready)
            markerManager = new (0, $ef2c6018bdbcf2f4$export$2e2bcd8739ae039)(mapManager, dataManager);
            // Initialize ClusterHeatManager (needs both map and data ready)
            clusterHeatManager = new (0, $3ff4ad7e21a96b8d$export$2e2bcd8739ae039)(mapManager, dataManager);
            // Wait for map to fully load before adding layers and markers
            mapManager.onLoad(()=>{
                // Add clustering and heatmap layers FIRST (loads before markers)
                clusterHeatManager.initialize();
                // Then add markers (heavier, loads after)
                markerManager.renderMarkers();
                // Set up coordinated zoom handler
                setupZoomCoordination();
                // Initialize FilterManager AFTER all visual components are ready
                setupFiltering();
            });
        } catch (error) {
            console.error("\u274C Error during initialization:", error);
            throw error;
        }
    }
    // ! Coordinate visibility between clusters/heatmap and markers based on zoom
    function setupZoomCoordination() {
        const map = mapManager.getMap();
        if (!map) return;
        map.on('zoom', ()=>{
            const currentZoom = map.getZoom();
            // Toggle cluster/heatmap visibility (visible < zoom 11)
            clusterHeatManager.toggleClustersVisibility(currentZoom);
            clusterHeatManager.toggleHeatmapVisibility(currentZoom);
        // MarkerManager handles its own visibility (visible >= zoom 11) through its internal zoom handler
        });
    }
    // ! Filtering Setup
    function setupFiltering() {
        // Initialize UIManager first (handles all panel interactions)
        uiManager = new (0, $b66ec90814aa65b3$export$2e2bcd8739ae039)(mapManager, markerManager, dataManager);
        uiManager.initialize();
        // Initialize FilterManager with all required managers
        filterManager = new (0, $99aaed0855512ef8$export$2e2bcd8739ae039)(dataManager, markerManager, clusterHeatManager);
        // Connect UIManager with FilterManager for project item visibility updates
        uiManager.setFilterManager(filterManager);
        // Expose globally for debugging/advanced use
        window.filterManager = filterManager;
        window.dataManager = dataManager;
        window.uiManager = uiManager;
        window.animationManager = animationManager;
        // ! Invoking Setup Functions
        filterManager.setupTypologyFilters();
        filterManager.setupTypologyAllButton(); // "all" button for typologies
        filterManager.setupStatusFilters();
        filterManager.setupStatusAllButton(); // "all-status" button for statuses
        filterManager.setupClearAllFiltersButton(); // "clear-filters" button
        filterManager.setupVisibleCounter(); // "filter-count" element
        // !Initialize AnimationManager for mobile drag handles and animations
        animationManager.initialize();
        // ! Initialize URLManager for URL parameter handling and browser navigation
        urlManager = new (0, $076dce5fefd24bda$export$2e2bcd8739ae039)(uiManager, markerManager, dataManager);
        urlManager.initialize();
        urlManager.hookIntoUIManager(); // Hook into project activation/deactivation
        window.urlManager = urlManager; // Expose for debugging
    }
    // ! Initialize everything
    initializeMap();
});

})();
//# sourceMappingURL=app.js.map
